import { Container } from 'ui';
import { Link, Typography } from 'omakase';
import { LocalDate } from 'js-joda';
import {
  conciergeTermsLink,
  privacyEmailAnchor,
  privacyPolicyLink,
  termsLink,
} from 'utils/legal/termsUtils';
import { sectionListItem, title } from 'routes/legal/termsCssDefs';
import { useHasDirectBrandUpdateFlags } from 'hooks/useHasDirectBrandUpdateFlags';
import Anchor from '../../components/Anchor';

const Terms: React.FunctionComponent = () => {
  const hasTwilioDirectBrandUpdatesFlag = useHasDirectBrandUpdateFlags();
  const applyUpdates =
    !LocalDate.now().isBefore(LocalDate.of(2026, 2, 12)) && hasTwilioDirectBrandUpdatesFlag;

  const effectiveDate = applyUpdates ? 'February 12, 2026' : 'June 10, 2025';
  const effectiveDateFrench = applyUpdates ? 'le 12 février 2026' : 'le 10 juin 2025';

  return (
    <div className="Legal">
      <Container maxWidth="md">
        <Typography css={title} component="h1" variant="heading01" align="center" mb={4}>
          Terms of Use
        </Typography>
        <Typography variant="body01Strong" mb={2}>
          Effective Date: {effectiveDate}
        </Typography>
        <Typography variant="body01Strong" mb={2}>
          Pour les résidents du Québec: Faites défiler la page pour consulter les conditions
          d'utilisation en français.
        </Typography>
        <Typography variant="body02" paragraph mb={2}>
          These Terms of Use (the “Terms”) constitute a binding legal agreement between Tock LLC
          (“Tock,” “we”, “us” or “our”) and any person (“you” or “your”) and cover your use of the
          sites, products, applications, tools and other services (collectively, the “Services”)
          provided by Tock, including without limitation on{' '}
          <Link href="https://www.exploretock.com">www.exploretock.com</Link> and any predecessor or
          successor websites established by Tock which reference the Terms and on Tock web, mobile
          and other applications.
        </Typography>
        <Typography variant="body02" paragraph mb={2}>
          Please read these Terms carefully! THEY INCLUDE IMPORTANT INFORMATION ABOUT YOUR LEGAL
          RIGHTS, AND COVER AREAS SUCH AS WARRANTY DISCLAIMERS, LIMITATIONS OF LIABILITY, THE
          RESOLUTION OF DISPUTES BY ARBITRATION AND A CLASS ACTION WAIVER.
        </Typography>
        <Typography variant="body02" paragraph mb={2}>
          By using or accessing the Services or authorizing another (including an individual acting
          as your concierge in accordance with our {conciergeTermsLink()}) to use or access the
          Services on your behalf, you're agreeing to these Terms (and that all actions taken by
          such person on your behalf are deemed to be actions taken by you). If you're using the
          Services for or on behalf of an organization, you're agreeing to these Terms on behalf of
          that organization, and you represent and warrant that you can do so. If you don't agree to
          all the terms in these Terms, you may not use or access the Services.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>1. Guests and Merchants</b>
        </Typography>
        <Typography variant="body02" paragraph mb={2}>
          Our Services can help you find and transact with restaurants, bars, wineries or other
          hospitality venues (each, a “Merchant”). If you are using the Services to transact with
          Merchants, you are a “Guest.” These Terms are solely between Tock and you, our Guest.
          Merchants are third parties and are not a party to these Terms. A Merchant's use of the
          Services is governed by a separate contract between Tock and each Merchant.
        </Typography>
        <Typography variant="body02" paragraph mb={2}>
          If you use the Services to act as a concierge for or on behalf of a Guest, you're agreeing
          to these Terms and our {conciergeTermsLink()}. Your breach of the Concierge Terms is a
          breach of these Terms.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>2. Creating Account(s)</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>2.1. Signing Up.</b> To use many of the Services, you must first create an account
            (“Account”). You agree to provide us with accurate, complete and at all times up to date
            information for your Account. We may need to use this information to contact you.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>2.2. Staying Safe.</b> Please safeguard your Account and make sure others don't have
            access to your Account or passwords and other authentication credentials (collectively,
            "passwords"). You're solely responsible for any activity on your Account and for
            maintaining the confidentiality and security of your passwords. We're not liable for any
            acts or omissions by you or anyone else in connection with your Account. You must
            immediately notify us by email to {privacyEmailAnchor()} if you know or have any reason
            to suspect that your Account or passwords have been stolen, misappropriated or otherwise
            compromised or in case of any actual or suspected unauthorized use of your Account.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>2.3. Eligibility, Suspension and Termination.</b> The Services are not intended for
            and may not be used by anyone under the age of 18. By using the Services, you represent
            that you're at least 18. We may, in our reasonable discretion, refuse to offer the
            Services to any person or entity, including cancellation of any booking or transaction,
            where required by law or where we have terminated your account. We reserve the right to
            suspend or terminate accounts with intentionally inaccurate information. We reserve the
            right to terminate all accounts that belong to a user in violation of these Terms.
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>3. Bookings</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>3.1. Bookings.</b> The Services enable you to make bookings with Merchants (each, a
            “Booking”). Bookings may consist of: (a) reserving an experience with a Merchant (each,
            a “Merchant Experience”); (b) placing an order for pick-up or delivery from a Merchant;
            (c) joining a waitlist for a Merchant Experience with a Merchant; (d) placing or paying
            for an order with a Merchant in person; or (e) any other kind of booking or purchase
            made available to you through the Services. To make a Booking with a Merchant, navigate
            to a Merchant-specific page on the Services (each, a “Merchant Page”), either select
            your requested experience, place an order or join a waitlist, and then follow
            instructions to complete your Booking. When you make a Booking, we will send you an
            email confirmation, including a unique confirmation number. You may be required by a
            Merchant to present your confirmation number and the credit card you used to make your
            Booking. If your Booking involves alcohol, you must also present a valid form of
            identification which shows that you are at least the minimum legal drinking age in the
            applicable jurisdiction, as required by applicable laws in such jurisdiction.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>3.2. Merchant Pricing, Availability and Terms.</b> Each Merchant (not Tock) is solely
            responsible for their Merchant Page, including, without limitation, the price,
            availability and other details of all Bookings listed by or on behalf of such Merchant.
            These details may be included on a Merchant Page and be applicable to all Bookings with
            a Merchant (“General Merchant Terms”) or specifically apply only to a particular Booking
            and be included as part of the information or explanation for such Booking (“Specific
            Merchant Booking Terms”). General Merchant Terms and/or Specific Merchant Booking Terms
            may include additional rules and restrictions related to a Merchant or a particular
            Booking, including: (a) details about Merchant's cancellation, refund and/or exchange
            policy/ies; (b) limitations on Merchant seating capacity; (c) limitations on Merchant
            Experience or other Booking order quantity; or (d) limitations on Merchant's ability to
            accommodate any dietary or other restrictions related to a Booking. Merchants may change
            the pricing for and availability of any Booking at any time with or without notice,
            including adding or removing Booking slots. Merchants may also change General Merchant
            Terms or Specific Merchant Booking Terms at any time. It is your responsibility to
            ensure that you have read the General Merchant Terms or Specific Merchant Booking Terms
            in effect at the time you make your Booking. Before you complete a Booking, you are
            responsible for reviewing the General Merchant Terms and Specific Merchant Booking Terms
            then in effect for such Merchant. For example, General Merchant Terms and/or Specific
            Merchant Booking Terms may disclose that: (i) some Bookings are final, non-refundable,
            non-transferable and/or non-exchangeable; and (ii) if you do not arrive at the
            Merchant's location on time or at all or fail to provide advance notice of cancellation
            of a Booking in accordance with Merchant's cancellation policy, you may subject to a
            no-show or late cancellation fee charged to the payment method used to secure such
            Booking. By completing a Booking, you agree to any and all General Merchant Terms or
            Specific Merchant Booking Terms applicable to your Booking. Tock shall have no liability
            whatsoever for any charges made to your payment method for any failure to cancel your
            Booking or otherwise comply with a Merchant's General Merchant Terms or Specific
            Merchant Booking Terms.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>3.3. Transferability of Bookings.</b> Your Booking may be transferable. You represent
            and warrant that your transfer of any Booking will comply with applicable laws in your
            jurisdiction. If you purchase a Booking from someone other than Tock, you do so at your
            own risk. Tock is not responsible for forgeries or misrepresentations. Unlawful resale
            (or attempted unlawful resale), counterfeiting or copying of a Booking is grounds for
            the seizure and cancellation of such Booking and any other Booking that has been made by
            you, without compensation to you. In addition, Tock reserves the right to restrict or
            deny Booking purchasing privileges to anyone that Tock determines to be, or has been, in
            violation of these Terms. Because Tock does not guarantee the authenticity of Bookings
            purchased from anyone other than Tock (including without limitation to brokers or other
            Tock users), Tock recommends that you only purchase Bookings directly through Tock.
            Bookings may not be used for advertising, promotions, contests or sweepstakes without
            the prior written consent of an authorized representative of Tock and the applicable
            Merchant.
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>4. Merchants</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>4.1. Merchant Responsibilities.</b> You hereby acknowledge and understand that each
            Merchant (and not Tock) is solely responsible for all aspects of such Merchant's
            business and operations, including provision of food, beverages and other aspects of a
            Booking, service, environment, safety, quality and accuracy.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>4.2. Merchant Compliance.</b> You hereby acknowledge and understand that each
            Merchant (and not Tock) is solely responsible and liable for all marketing, selling,
            pricing, packaging, and provision of any products, services or other Bookings offered to
            Guests through the Services in compliance with all applicable laws. Tock makes no
            representation or warranty regarding whether a Merchant holds any applicable permit,
            license, registration or other credential for its business; whether representations by a
            Merchant are true and accurate; or whether a Merchant complies with applicable laws, and
            Tock is not responsible for the quality of the products or services provided by
            Merchants.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>4.3. Release.</b> Merchants are solely responsible for any and all damages, losses,
            liabilities, costs, claims, injuries, demands, fines, awards and expenses of any kind
            (including without limitation reasonable attorneys' fees and costs) (collectively,
            "Losses") suffered by you as a result of your interaction with or visit to any Merchant
            or from any Booking, promotion, offer, product or service of any Merchant. You must
            resolve all disputes directly with Merchant. To the maximum extent permitted by
            applicable law, you hereby release Tock, our affiliates, and our respective officers,
            directors, employees and agent (collectively, the “Tock Parties”) from any and all such
            Losses. IN CONNECTION WITH THE FOREGOING, IF YOU ARE A CALIFORNIA RESIDENT, YOU HEREBY
            WAIVE CALIFORNIA CIVIL CODE SECTION 1542, WHICH STATES: “A GENERAL RELEASE DOES NOT
            EXTEND TO CLAIMS WHICH THE CREDITOR DOES NOT KNOW OR SUSPECT TO EXIST IN HIS OR HER
            FAVOR AT THE TIME OF EXECUTING THE RELEASE, WHICH, IF KNOWN BY HIM OR HER MUST HAVE
            MATERIALLY AFFECTED HIS OR HER SETTLEMENT WITH THE DEBTOR.” You hereby expressly waive
            and relinquish all rights and benefits under that section and any law of any
            jurisdiction of similar effect with respect to the release of any unknown or unsuspected
            claims you may have against the Tock Parties pertaining to the subject matter of this
            Section 4.3. If you are a resident of the United Kingdom or European Union, this release
            shall not apply to any damage arising from our willful misconduct or gross negligence,
            nor shall it apply to damage from injury to life, body or health.
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>5. Paid Bookings</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>5.1. Paid Bookings.</b> If you wish to make a Booking which requires payment of money
            to a Merchant or otherwise requires a debit or credit card to secure such Booking (each,
            a “Paid Booking”), you will be asked by Tock or a third party payment processor to
            supply payment information (“Payment Information”). You agree that all Payment
            Information that you provide to Tock or such third party will be accurate, current and
            complete. You agree to pay all Paid Booking charges incurred by you or any users of your
            Account and/or credit card (or other payment mechanism) at the prices in effect at the
            time of the purchase or as otherwise agreed by you, including any applicable Merchant
            Fees, Tock Fees and Taxes (as defined below). “Merchant Fees” means any and all service
            charges or other fees for a particular Paid Booking which are configured and will be
            received by the Merchant. “Tock Fees” means any and all service charges or other fees
            for a particular Paid Booking which will be received by Tock. Tock Fees are
            non-refundable. Merchant Fees and Tock Fees will be listed as separate line items during
            checkout. Tock accepts several possible methods of payment to enable you to make a Paid
            Booking. You represent and warrant that you have the legal right to use any credit card
            or other payment mechanism used in any Paid Booking. By submitting such Payment
            Information, you grant Tock the right to provide such Payment Information to a third
            party for the purposes of facilitating the completion of your Paid Booking. Verification
            of Payment Information may be required prior to the completion of any Paid Booking.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>5.2. Tock as Payment Collection Agent.</b> Payments you make to a Merchant via the
            Services may be submitted to Tock, and in such event, Tock will act as such Merchant's
            payment collection agent solely for the limited purpose of accepting and processing
            payments from you. The delivery of your payment to Tock (acting as the applicable
            Merchant's payment collection agent), shall satisfy your obligations to the Merchant.
            Once such a payment is received by Tock on such Merchant's behalf, such Merchant has
            authorized Tock to electronically credit the funds into a bank account designated by
            such Merchant. In the event that Tock does not remit any such amounts, such Merchant
            will have recourse only against Tock and not against you directly.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>5.3. Marketplace Facilitator and Taxes.</b> Tock may qualify a “marketplace
            facilitator” in certain jurisdictions. A marketplace facilitator is commonly defined by
            United States (“U.S.”) state laws as a business that contracts with third party sellers
            to promote the sale of physical property, digital goods and/or services owned and
            supplied by a third party through an online website. Where Tock is a marketplace
            facilitator, Tock is responsible for calculating, collecting, and remitting sales and
            use Taxes. Tock will charge and collect sales and use tax on taxable transactions and
            you are responsible for payment of such sales and use tax if charged. All fees for Paid
            Bookings (including any applicable Merchant Fees and Tock Fees) are exclusive of
            applicable national, provincial, state, local or other taxes (“Taxes”), unless
            explicitly stated otherwise.
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>6. Privacy Policy</b>
        </Typography>
        <Typography variant="body02" paragraph mb={2}>
          By using the Services, you confirm that you have read and understood our{' '}
          {privacyPolicyLink()}. Your access and use of the Services is governed by Tock's Privacy
          Policy, which is incorporated into these Terms by reference.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>7. Third Party Services and Merchant Pages</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>7.1. Third Party Services.</b> The Services (including Merchant Pages) may link to or
            be integrated with various third party websites, services or applications (collectively,
            “Third Party Services”) that may make their content, products or services available to
            you. Merchant Pages often include links to Third Party Services related to that specific
            Merchant. Examples of Third Party Services include articles about a Merchant, a
            Merchant's website, articles about the hospitality industry, and payment processors. If
            you click on a link to a Third Party Service, you may leave the Services and go to the
            Third Party Service you selected. These Third Party Services may have their own terms
            and policies, and your use of them will be governed by those terms and policies.{' '}
            <b>
              We don't control Third Party Services, and we're not liable for Third Party Services,
              for any transaction you may enter into with them, or for what they do.
            </b>
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>7.2. Content.</b> The Services (including Merchant Pages) may contain content,
            including without limitation text, photos, images, music, audio, videos, logos and
            associated materials (“Content”) of a Merchant (“Merchant Content”) and/or potential
            Merchant Experiences: (a) which are offensive or objectionable; (b) which contain
            errors; or (c) the downloading, copying or use of which is subject to additional terms
            and policies of third parties or is protected by intellectual property, trade secret,
            privacy or other laws. Tock is not responsible for any Merchant Content or Merchant
            Experiences.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>7.3. No Endorsement.</b> Descriptions or images of, or references to, Third Party
            Services or Merchant Experiences from the Services do not imply Tock's endorsement
            thereof. Tock does not provide, endorse or guarantee any Third Party Service or Merchant
            Experience. Merchants are third parties and Tock is not affiliated with any Merchant nor
            is Tock responsible for Merchant Content or Merchant Experiences. By operating the
            Services, we don't represent or imply that we endorse Merchant Content or Merchant
            Experiences, or that we believe such Merchant Content or Merchant Experiences (nor any
            descriptions or images thereof, or references thereto) to be accurate, complete,
            reliable, current, lawful or non-harmful. We're not a publisher of, and we're not liable
            for, any Merchant Content uploaded, posted, published or otherwise made available on a
            Merchant Page.
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>8. Intellectual Property & Services Restrictions</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>8.1. Use of the Services.</b> We grant you permission to use the Services solely as
            set forth in these Terms. Without the prior written consent of a duly authorized
            representative of Tock, you agree not to: (a) copy or distribute any part of the
            Services in any medium; (ii) alter or modify any part of the Services other than as may
            be reasonably necessary to use the Services for their intended purpose; or (c)
            dissemble, decompile or reverse engineer any part of the Services.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>8.2. Ownership of the Services.</b> Except for Merchant Content, the Services are
            owned by Tock, and are protected by copyright, trade secret, trademark and other U.S.
            and foreign laws. These Terms don't grant you any right, title or interest in the
            Services, Merchant Content, Tock trademarks, logos or other Content, brand features or
            intellectual property or trade secrets. You agree not to change, modify, translate or
            otherwise create derivative works of the Services or any Content thereon (including
            Merchant Content).
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>8.3. User Content.</b> The Services may permit you to submit, transmit, post or
            otherwise provide content, including, but not limited to, reviews, comments, ratings,
            photos, images, videos, sounds, text, data, links and location information (“User
            Content”). Any such User Content must not: (a) be illegal, threatening, obscene, racist,
            defamatory, libelous, pornographic, infringing of intellectual property rights,
            promoting of illegal activity or harm to groups and/or individuals, invasive of a third
            party's publicity or privacy rights, purposely false or otherwise injurious to third
            parties, or objectionable; or (b) consist of or contain software, computer viruses,
            commercial solicitation, political campaigning, chain letters, mass mailings, any form
            of “spam” or references to illegal activity, malpractice, purposeful overcharging, false
            advertising, or health code violations (e.g., foreign objects in food, food poisoning,
            etc.). Your User Content should be unbiased and objective. You may not submit reviews,
            comments or ratings for which you are being compensated in any manner, or for your own
            restaurant or any restaurant of your employer, friend, relative or a competitor. You may
            not use a false email address, impersonate any person or entity, or otherwise mislead as
            to the origin of User Content. The name or handle you choose to provide to Tock may be
            displayed publicly with such User Content. Tock reserves the right (but has no
            obligation) to monitor, remove, or edit User Content in Tock's sole discretion,
            including if User Content violates these Terms, but you acknowledge that Tock may not
            regularly review submitted User Content. If you do submit User Content, and unless we
            indicate otherwise, you grant Tock a nonexclusive, perpetual, royalty-free, irrevocable,
            and fully sublicensable (through multiple tiers, including to restaurants, partners and
            other third party websites and feeds) right to use, modify, reproduce, adapt, translate,
            publish, create derivative works from, distribute, display, and otherwise exploit such
            User Content throughout the world in any media, and, where applicable, you hereby waive
            any privacy or publicity rights or any similar rights in an individual's name or
            likeness in addition to any moral or other rights you may have in the User Content you
            submit, in favor of Tock. You represent that you own, or have the necessary permissions
            to use and authorize the use of User Content as described herein. Tock takes no
            responsibility and assumes no liability for any User Content submitted by you or any
            other User or third party, nor do we guarantee any confidentiality with respect to User
            Content.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>8.4 Do Not Crawl the Services.</b> Robots, spiders, scripts, scrapers, crawlers, and
            all similar technologies are strictly prohibited. You agree not to use or provide
            software (except for general purpose web browsers and email clients, or software
            expressly licensed by Tock) or services that circumvent, interact, or interoperate with
            our Services in ways that are prohibited by these Terms. The use by you, or anyone
            authorized by you, of machines, computers, scripts or any automated system on the Site
            is strictly prohibited and may result in criminal liability under the Computer Fraud and
            Abuse Act, Section 1030 of the U.S. Code and/or under applicable laws in other
            jurisdictions.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>8.5 Feedback.</b> We welcome your feedback, ideas or suggestions (collectively,
            “Feedback”), but you agree that we may use your Feedback without any restriction or
            obligation to you, even after these Terms are terminated. This Section does not limit or
            affect any rights you may have under applicable data protection laws.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>8.6 Other Services Restrictions.</b> You agree not to: (a) use the Services for any
            unauthorized purpose including collecting or harvesting any information about Tock or
            our Merchants or Guests, by electronic or other means, for any purpose, including
            without limitation to send unsolicited email or other electronic communications or
            engage in unauthorized framing of, or linking to, the Services without our express
            written consent; (b) use the Services in a way which attempts to: (i) impersonate any
            other person or entity; (ii) sell or let others use your profile or password; (iii)
            provide false or misleading identification or similar information; or (iv) invade the
            privacy or violate the personal or proprietary right of any person or entity; or (c) use
            the Services in a way which might inhibit other Guests from using or enjoying the
            Services or making a Booking. Furthermore, you represent and warrant that your use of
            the Services is in compliance with applicable laws, including without limitation
            applicable export or import controls and regulations and sanctions.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <Typography mb={2}>
              <b>8.7 Copyright Complaints (Digital Millennium Copyright Act).</b> In operating the
              Services, we may act as a “service provider” as defined by Digital Millennium
              Copyright Act (“DMCA”) and offer services as online provider of materials and links to
              third party websites. As a result, third party materials that we do not own or control
              may be transmitted, stored, accessed, or otherwise made available using the Services.
              Tock has in place certain legally mandated procedures regarding allegations of
              copyright infringement occurring on the Services. If you believe any material
              available via the Services infringes a copyright, you should notify us using the
              notice procedure for claimed infringement under the DMCA (17 U.S.C. Sect. 512(c)(2)).
              We will respond expeditiously to remove or disable access to the material claimed to
              be infringing and will follow the procedures specified in the DMCA to resolve the
              claim between the notifying party and the alleged infringer who provided the material.
              Our designated agent (i.e., proper party for notice) to whom you should address
              infringement notices under the DMCA is American Express’s intellectual property
              counsel at <Anchor href="mailto:DMCA@aexp.com">DMCA@aexp.com</Anchor>.
            </Typography>
            <Typography>Please provide the following notice:</Typography>
            <Typography component="ul">
              <Typography component="li" css={sectionListItem}>
                1. Identify the copyrighted work or other intellectual property that you claim has
                been infringed;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                2. Identify the material on the Services that you claim is infringing, with enough
                detail so that we may locate it on the Services;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                3. A statement by you that you have a good faith belief that the disputed use is not
                authorized by the copyright owner, its agent, or the law;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                4. A statement by you declaring under penalty of perjury that (a) the above
                information in your notice is accurate, and (b) that you are the owner of the
                copyright interest involved or that you are authorized to act on behalf of that
                owner;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                5. Your address, telephone number, and email address; and
              </Typography>
              <Typography component="li" css={sectionListItem}>
                6. Your physical or electronic signature.
              </Typography>
            </Typography>
            <Typography>
              We may give notice to our users of any infringement notice by means of a general
              notice on the Services, electronic mail to a user's e-mail address in our records, or
              by written communication sent by first-class mail to a user's physical address in our
              records. If you receive such an infringement notice, you may provide
              counter-notification in writing to the designated agent that includes the information
              below. To be effective, the counter-notification must be a written communication that
              includes the following:
            </Typography>
            <Typography component="ul">
              <Typography>1. Your physical or electronic signature;</Typography>
              <Typography component="li" css={sectionListItem}>
                2. Identification of the material that has been removed or to which access has been
                disabled, and the location at which the material appeared before it was removed or
                access to it was disabled;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                3. A statement from you under the penalty of perjury, that you have a good faith
                belief that the material was removed or disabled as a result of a mistake or
                misidentification of the material to be removed or disabled; and
              </Typography>
              <Typography component="li" css={sectionListItem}>
                4. Your name, physical address and telephone number, and a statement that you
                consent to the jurisdiction of a Federal District Court for the judicial district in
                which your physical address is located, or if your physical address is outside of
                the United States, for any judicial district in which we may be found, and that you
                will accept service of process from the person who provided notification of
                allegedly infringing material or an agent of such person.
              </Typography>
            </Typography>
            <Typography>The DMCA applies in the United States only.</Typography>
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>9. Important Things We Can Do.</b> We reserve these rights, which we may exercise at
          any time and in our sole discretion, and without liability or notice to you (except where
          prohibited by applicable law): (a) we may change parts or all of the Services and their
          functionality; (b) we may suspend or discontinue parts or all of the Services; (c) we may
          terminate, suspend, restrict or disable your access to or use of parts or all of the
          Services; (d) we may terminate, suspend, restrict or disable access to your Accounts or
          parts or all of your Bookings; (e) we may honor or impose conditions on the honoring of
          any coupon, coupon code, promotional code or other similar promotions; (f) we may bar any
          Account from making any or certain Bookings; (g) we may refuse to provide any Account from
          making a particular Booking; and (h) we may change our eligibility criteria to use the
          Services (and if such eligibility criteria changes are prohibited by law where you reside,
          we may revoke your right to use the Services in that jurisdiction).
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>10. Term and Termination.</b> This Agreement will remain in effect until terminated by
          either you or us, and you agree that Tock shall not be liable to you or any third party
          for any such termination. You may terminate this Agreement at any time via the Services.
          We reserve the right to change, suspend or discontinue, or terminate, restrict or disable
          your use of or access to, parts or all of the Services or their functionality at any time
          at our sole discretion and without notice. For example, we may suspend or terminate your
          use of part or all of the Services if you violate these Terms. We will endeavor to provide
          you reasonable notice upon suspending or terminating part or all of the Services. We also
          reserve the right to remove any Merchants from the Services at any time at our sole
          discretion. In the unlikely event of any such removal, we may need to cancel any Bookings
          you made with such Merchants. We will notify you of any such cancellations. All sections
          of this Agreement that by their nature should survive termination shall survive
          termination, including without limitation the following sections in these Terms and any
          similar sections or provisions in the rest of this Agreement: Bookings, Merchants, Paid
          Bookings, Release, Disclaimers, Limitation of Liability, Indemnification, Dispute
          Resolution and Additional Terms.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>11. Disclaimers.</b> To the fullest extent permitted by applicable law, Tock makes no
          warranties, either express or implied, about the Services, any Merchant Content or User
          Content thereon or any Merchant. The Services and all Merchant Content or User Content
          thereon are provided “as is” and “as available.” Tock also disclaims any warranties of
          merchantability, fitness for a particular purpose and non-infringement. No advice or
          information, whether oral or written, obtained by you from Tock, shall create any
          warranty. Tock makes no warranty or representation: (a) about the accuracy or completeness
          of the Services or any Merchant Content or User Content thereon; or (b) that the Services
          or any Merchant Content or User Content thereon will: (i) be timely, uninterrupted or
          error-free; (ii) meet your requirements or expectations; or (iii) be free from viruses or
          other harmful components.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>12. Limitation of Liability.</b> You acknowledge and agree that to the fullest extent
          permitted by applicable law, in no event will the Tock Parties be liable with respect to
          any claims arising out of or related to the Services or this Agreement for: (a) any
          indirect, special, incidental, exemplary, punitive or consequential damages; (b) any loss
          of profits, revenue, data, goodwill or other intangible losses; (c) any Losses (as defined
          below) related to your access to, use of or inability to access or use parts, some or all
          of your Account, your Bookings or parts or all of the Services, including without
          limitation interruption of use or cessation or modification of any aspect of the Services;
          (d) any Losses related to unavailability, degradation, loss, corruption, theft,
          unauthorized access or, unauthorized alteration of, any content, information or data; (e)
          any Merchant Content or other conduct or content of any user or third party using the
          Services, including without limitation defamatory, offensive or unlawful conduct or
          content; or (f) any Third Party Services or third party sites accessed via the Services.
          These limitations apply to any theory of liability, whether based on warranty, contract,
          tort, negligence, strict liability or any other legal theory, whether or not Tock has been
          informed of the possibility of such damage, and even if a remedy set forth in this
          Agreement is found to have failed its essential purpose. To the fullest extent permitted
          by applicable law, in no event shall the aggregate liability of Tock for all claims
          arising out of or related to the Services and this Agreement exceed the greater of twenty
          dollars ($20) or the amounts paid by you to Tock, whether directly, or acting as payment
          collection agent on behalf of one or more Merchants, in the three (3) months immediately
          preceding the event that gave rise to such claim.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>13. Indemnification.</b> To the fullest extent permitted by law, you agree to indemnify
          and hold harmless Tock and its affiliates and its and their directors, officers, employees
          and agents from and against all Losses arising out of or related to: (a) your violation of
          these Terms; (b) your use and access of the Services or any Content thereon; or (c) your
          violation of any law or regulation or the rights or good name of any third party. Tock
          shall have the right, but not the obligation, to participate in the defense of such
          actions with the counsel of its choosing. Your indemnification obligations under this
          Section shall not apply to the extent directly caused by our breach of this Agreement.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>14. Dispute Resolution</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>14.1. Informal Resolution.</b> Before filing a claim against Tock, you agree to try
            to resolve the dispute by sending notice of your claim to American Express ADR c/o CT
            Corporation System, 28 Liberty Street, New York, New York 10005. Your{' '}
            <u>claim notice should include</u> a description of your claim and proof of your
            relationship with Tock. If we can't resolve the dispute within ninety (90) days of our
            receipt of your first claim notice, you or Tock may commence an arbitration proceeding.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>14.2. Arbitration Agreement.</b>
            <br />
            <br />
            <Typography component="ul" paragraph mb={2}>
              <Typography component="li" css={sectionListItem}>
                <b>
                  14.2.1. Unless you opt-out during the Opt-Out Period in accordance with Section
                  14.3, you and Tock agree to resolve any claims, disputes and matters arising out
                  of or in connection with these Terms (including without limitation its existence,
                  formation, operation and termination) and/or the Services (including without
                  limitation non-contractual disputes and matters) through final and binding
                  arbitration and you and Tock expressly waive the right to formal court proceedings
                  ( including without limitation trial by jury), except as set forth in this
                  Section. Discovery and rights to appeal in arbitration are generally more limited
                  than in a lawsuit, and other rights that you and we would have in court may not be
                  available in arbitration. There is no judge or jury in arbitration, only an
                  experienced, independent third party that will act as the arbitrator, and court
                  review of an arbitration award is limited.
                </b>
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>14.2.2.</b> The arbitrator shall be empowered to grant whatever relief would be
                available in a court under law or in equity. The arbitrator further has the right to
                impose sanctions, in accordance with the rules of the American Arbitration
                Association (“AAA”), if any, including for: (a) any frivolous claims or submissions
                the arbitrator determines have not been filed in good faith; or (b) a party's
                failure to comply with this Section 14. For avoidance of doubt, the right to impose
                sanctions includes the right to shift arbitration fees if permitted by the AAA
                Rules.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>14.2.3.</b> Any arbitration demand or counterclaim asserted by either party must
                contain sufficient information to provide fair notice to the other party of the
                asserting party's identity, the claims being asserted and the factual allegations on
                which those claims are based, and must include proof that the claimant is party to
                these Terms. The arbitrator may require amendment of any demand or counterclaim that
                does not satisfy these requirements. The arbitrator has the right to impose
                sanctions for any claims the arbitrator determines to be frivolous or improper
                (under the standard set forth in Federal Rule of Civil Procedure 11 and any similar
                standards in other jurisdictions), including for any claim filed on behalf of a
                claimant who is not a party to these Terms.
              </Typography>
            </Typography>
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>
              14.3. Arbitration Opt-Out. You can decline this agreement to arbitrate by sending us a
              rejection notice at American Express, P.O. Box 981556, El Paso, TX 79998 within thirty
              (30) days of the date that you first agree to these Terms (“Opt-Out Period”). Your
              rejection notice must include your full name, residential address and a clear
              statement that you want to opt out of arbitration. If you opt out of arbitration
              pursuant to this Section 14.3, then Sections 14.2, 14.4, 14.5 and 14.6 of these Terms
              do not apply to you. This opt-out doesn't affect any other sections of the Terms,
              including without limitation Sections 14.8 (Time for Filing), 14.9 (No Class Actions)
              and 15 (Controlling Law; Judicial Forum for Disputes).
            </b>
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>14.4. Arbitration Time For Filing.</b> Any arbitration must be commenced by filing a
            demand for arbitration within one (1) year after the date the party asserting the claim
            first knows or reasonably should know of the act, omission or default giving rise to the
            claim. If applicable law prohibits a one (1) year limitation period for asserting
            claims, any claim must be asserted within the shortest time period permitted by
            applicable law.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>14.5. Arbitration Procedures.</b> The arbitration will be administered by the
            American Arbitration Association (“AAA”), 800-778-7879 (toll-free) Website:{' '}
            <Link href="https://www.adr.org/">www.adr.org</Link>, and resolved before a single
            arbitrator. If AAA is not available to arbitrate, you and Tock will mutually agree on an
            alternative arbitration provider. Except as modified by this Section 14, AAA will
            administer the arbitration in accordance with its dispute resolution rules and
            procedures in effect and applicable at the time any demand for arbitration is filed (the
            "AAA Rules”), but excluding any rules or procedures governing or permitting class or
            representative actions. Each party is responsible for its own attorneys' fees, except to
            the extent otherwise provided by the AAA Rules, the arbitrator and/or applicable law.
            Judgment upon the award rendered by such arbitrator may be entered in any court of
            competent jurisdiction. The “arbitrator” as used in this Section 14 shall be understood
            to include AAA. You and Tock agree that: (a) these Terms affect interstate commerce, so
            the US Federal Arbitration Act (“FAA”) and federal arbitration law apply and govern the
            interpretation and enforcement of this Section 14 (despite Section 15 below); and (b)
            any arbitration hearings shall occur at a location to be agreed upon in New York, New
            York, be administered by AAA in English, and shall be settled by one (1) commercial
            arbitrator with substantial experience in resolving intellectual property and commercial
            contract disputes, who shall be selected from the appropriate list of AAA arbitrators in
            accordance with the AAA Rules.
            <br />
            <br />
            <Typography component="ul" paragraph mb={2}>
              <Typography component="li" css={sectionListItem}>
                <b>14.5.1. No Class or Consolidated Arbitration Absent Written Consent.</b> Except
                to the extent Tock in our sole discretion consents in writing, Tock does not agree
                or consent under any circumstances to class arbitration, private attorney general
                arbitration, or arbitration involving joint or consolidated claims.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>14.5.2. Severability of Claims.</b> If there is a final judicial determination
                that precludes enforcement of this Section 14's limitations as to a particular
                claim, remedy, or request for relief, then such claim, remedy, or relief (and only
                such claim, remedy, or relief) must be severed from the arbitration and may be
                sought in court. The parties agree, however, that any adjudication of any remaining
                claims, remedies, or relief not subject to arbitration shall be stayed pending the
                outcome of any arbitrable claims and remedies. This Section 14.5.2 does not prevent
                you or Tock from participating in a class-wide settlement of claims.
              </Typography>
            </Typography>
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>14.6. Arbitration Fees.</b> The AAA Rules will govern payment of all arbitration
            fees. We won't seek our attorneys' fees and costs in arbitration unless the arbitrator
            determines that your claim is frivolous. You and Tock agree that AAA has discretion
            where it deems appropriate to reduce the amount or modify the timing of any
            administrative or arbitration fees due under the applicable AAA Rules, provided that
            such modification does not increase the costs to you, and you further agree that you
            waive any objection to such fee modification. You and Tock also agree that a good faith
            challenge by either party to the fees imposed by AAA does not constitute a default,
            waiver, or breach of this Section 14 while such challenge remains pending before an
            arbitrator and/or a court of competent jurisdiction, and that any and all due dates for
            those fees shall be tolled during the pendency of such challenge.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>14.7. Exceptions To Arbitration Agreement.</b> Notwithstanding anything in these
            Terms, either you or Tock may bring a lawsuit solely for injunctive relief to stop
            unauthorized use or abuse of the Services, or intellectual property infringement or
            misappropriation (for example, trademark, trade secret, copyright or patent rights)
            without first engaging in arbitration or the informal dispute resolution process
            described above. Either you or Tock may assert claims, if they qualify, in small claims
            court in New York,New York or any US county where you reside or work.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>14.8. Time For Filing.</b> Any claim not subject to arbitration must be commenced
            within one (1) year after the date the party asserting the claim first knows or
            reasonably should know of the act, omission or default giving rise to the claim. If
            applicable law prohibits a one (1) year limitation period for asserting claims, any
            claim must be asserted within the shortest time period permitted by applicable law.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>14.9. NO CLASS ACTIONS.</b> You may only resolve disputes with us on an individual
            basis, and may not bring a claim as a plaintiff or a class member in a class,
            collective, consolidated or representative action. Class actions, class arbitrations,
            collective actions, private attorney general actions and consolidation with other
            arbitrations aren't allowed, except to the extent Tock in our sole discretion consents
            in writing pursuant to Section 14.5.1.
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>15. Controlling Law; Judicial Forum for Disputes.</b> These Terms (including their
          existence, formation, operation and termination) and the Services as well as all disputes
          and matters arising out of or in connection with these Terms and the Services (including
          non-contractual disputes and matters) shall be governed in all respects by the laws of the
          State of New York, without regard to its conflict of law provisions, except that the FAA
          shall prevail to the extent that there exists any conflict between the FAA and the laws of
          the State of New York with respect to Section 14. If Section 14 is found not to apply to
          you or your claim, or if you opt out of arbitration pursuant to Section 14.3, you and Tock
          agree that any judicial proceeding (other than small claims actions) arising out of or in
          connection with these Terms (including its existence, formation, operation and
          termination) and/or the Services (including non-contractual disputes and matters) must be
          brought exclusively in the federal or state courts of New York County, New York and you
          and Tock consent to venue and personal jurisdiction in such courts.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>16. Additional Terms</b>
        </Typography>
        <Typography component="ul" paragraph>
          <Typography component="li" css={sectionListItem}>
            <b>16.1. Our Mobile Apps.</b> You acknowledge that these Terms are a binding legal
            contract between you and Tock only, and not with a mobile app store company such as
            Apple Inc. or Google LLC, and that Tock and not such app store company, is solely
            responsible for the Services and the content and support thereof.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>16.2. Electronic Communications.</b> Tock will communicate with you by electronic
            means, including without limitation via email, text or push notification. You hereby
            agree that all notices, disclosures, agreements and other communications that we provide
            to you electronically satisfy any legal requirement that such communications be in
            writing.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>16.3. Entire Agreement.</b> These Terms constitute the entire agreement between you
            and Tock regarding the subject matter of these Terms, and supersede and replace any
            other prior or contemporaneous agreements, or terms and conditions applicable to the
            subject matter of these Terms. You agree that you have not relied upon, and have no
            remedies in respect of, any term, condition, statement, warranty or representation
            except those expressly set out in these Terms. You also may be subject to additional
            terms, policies or agreements that may apply when you use other services, including
            Third Party Services. These Terms create no third party beneficiary rights, and no third
            party shall have any right or standing to claim benefit or bring an action to enforce
            these Terms (except as expressly contemplated herein or otherwise agreed upon in
            additional terms between you and a Tock affiliate that sets forth such Tock affiliate's
            third party beneficiary rights to enforce these Terms). writing.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>16.4. Waiver, Severability And Assignment.</b> Our failure or delay to enforce any
            provision of these Terms is not a waiver of our right to do so later. If any provision
            of these Terms is found unenforceable, the remaining provisions will remain in full
            effect and an enforceable term will be substituted reflecting our intent as closely as
            possible. You may not delegate, transfer or assign these Terms or any of your rights or
            obligations hereunder without our prior written consent, and any such attempt will be of
            no effect. We may delegate, transfer or assign these Terms or some or all of our rights
            and obligations hereunder, in our sole discretion, to any of our affiliates or
            subsidiaries or to any purchaser of any of our business or assets associated with the
            Services, with thirty (30) days prior written notice.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>16.5. Modifications.</b> We may modify these Terms from time to time, and will post
            the most current version on our site. If a modification meaningfully reduces your
            rights, we'll notify you (by, for example, sending you an email or displaying a
            prominent notice within the Services). The notice will designate a reasonable period
            after which the new terms will take effect. Modifications will not apply retroactively.
            By continuing to use or access the Services after any modifications come into effect,
            you agree to be bound by the modified Agreement and price changes. If you disagree with
            our changes, then you must stop using the Services and cancel all Bookings.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>16.6. Events Beyond Our Control.</b> We are not in breach of these Terms or liable to
            you if there is any total or partial failure of performance of the Services resulting
            from any act, circumstance, event or matter beyond our reasonable control. This may
            include where such results from any act of God, fire, act of government or state or
            regulation, war, civil commotion, terrorism, pandemic, insurrection, inability to
            communicate with third parties for whatever reason, failure of any computer dealing or
            necessary system, failure or delay in transmission of communications, failure of any
            internet service provider, strike, industrial action or lock-out or any other reason
            beyond our reasonable control.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>16.7. Translation.</b> These Terms were originally written in English. We may
            translate these Terms into other languages. In the event of a conflict between a
            translated version and the English version, the English version will control except
            where prohibited by applicable law.
          </Typography>
        </Typography>

        {hasTwilioDirectBrandUpdatesFlag && (
          <>
            <Typography variant="body02" paragraph mb={2}>
              <b>17. Text Messaging</b>
            </Typography>
            <Typography component="ul" paragraph mb={2}>
              <Typography component="li" css={sectionListItem}>
                <b>17.1</b> By providing your mobile number, you agree to the Tock {termsLink()} and
                {privacyPolicyLink()}, and your mobile number may be used to send you text messages
                and call you, through the use of an auto dialer, artificial, and/or prerecorded call
                or text, to service your Tock account including to confirm and manage reservations.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>17.2</b> You can cancel SMS service at any time by replying STOP. After you send
                the STOP SMS message, you'll be sent an SMS message to confirm you are unsubscribed.
                Once confirmed, you will no longer receive SMS messages from us. If you want to join
                again, reply START on that same text conversation or submit a request{' '}
                <Anchor href="https://www.surveymonkey.com/r/VQGTSY5">here</Anchor> if more
                assistance is needed.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>17.3</b> If you are experiencing issues with the messaging program, you can get
                help <Anchor href="https://www.surveymonkey.com/r/VQGTSY5">here</Anchor>.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>17.4</b> Carriers and Tock are not liable for delayed or undelivered messages.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>17.5</b> As always, message and data rates may apply for any messages sent to you
                from us and to us from you. Message frequency varies. If you have any questions
                about your text plan or data plan, it is best to contact your wireless provider.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>17.6</b> If you have any questions regarding privacy, please read our{' '}
                {privacyPolicyLink()}
              </Typography>
            </Typography>
          </>
        )}
        <br />
        <br />

        <Typography css={title} component="h1" variant="heading01" align="center" mb={4}>
          Modalités d'utilisation
        </Typography>
        <Typography variant="body01Strong" mb={2}>
          Date d'entrée en vigueur : {effectiveDateFrench}
        </Typography>
        <Typography variant="body02" paragraph mb={2}>
          Les présentes modalités d'utilisation (les « modalités ») constituent un accord juridique
          contraignant entre Tock LLC (« Tock », « nous », « notre » ou « nos ») et toute personne
          (« vous », « votre » ou « vos »), et couvrent votre utilisation des sites, produits,
          applications, outils et autres services (collectivement, les « services ») fournis par
          Tock, y compris mais sans s'y limiter, sur{' '}
          <Link href="https://www.exploretock.com">www.exploretock.com</Link> et tout site Web
          prédécesseur ou successeur établi par Tock qui fait référence aux modalités, ainsi que sur
          les applications Web, mobiles et autres de Tock.
        </Typography>
        <Typography variant="body02" paragraph mb={2}>
          Veuillez lire attentivement les présentes modalités. ELLES CONTIENNENT DES RENSEIGNEMENTS
          IMPORTANTS SUR VOS GARANTIES JURIDIQUES ET COUVRENT DES DOMAINES TELS QUE LES DÉNIS DE
          GARANTIE, LES LIMITATIONS DE RESPONSABILITÉ, LE RÈGLEMENT DES CONTESTATIONS PAR ARBITRAGE
          ET LES RENONCIATIONS À TOUT RECOURS COLLECTIF.
        </Typography>
        <Typography variant="body02" paragraph mb={2}>
          En utilisant les services ou y en accédant, ou en autorisant une autre personne (y compris
          une personne agissant en tant que concierge conformément à nos{' '}
          {conciergeTermsLink('modalités relatives aux concierges')}) à les utiliser ou à y accéder
          en votre nom, vous acceptez les présentes modalités (et toutes les mesures prises par
          cette personne en votre nom sont réputées être des mesures prises par vous). Si vous
          utilisez les services pour ou au nom d'une organisation, vous acceptez les présentes
          modalités au nom de cette organisation, et vous déclarez et garantissez que vous pouvez
          les utiliser. Si vous n'acceptez pas la totalité des présentes modalités, vous ne pouvez
          ni utiliser les services ni y accéder.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>1. Clients et marchands</b>
        </Typography>
        <Typography variant="body02" paragraph mb={2}>
          Nos services peuvent vous aider à trouver des restaurants, des bars, des établissements
          vinicoles ou d'autres lieux d'accueil (chacun étant un « marchand ») et à effectuer des
          transactions auprès d'eux. Si vous utilisez les services pour effectuer des transactions
          avec des marchands, vous êtes un « client ». Les présentes modalités sont exclusivement
          conclues entre Tock et vous, notre client. Les marchands sont des tiers et ne sont pas
          parties aux présentes modalités. L'utilisation des services par un marchand est régie par
          un contrat distinct entre Tock et chaque marchand.
        </Typography>
        <Typography variant="body02" paragraph mb={2}>
          Si vous utilisez les services pour agir en tant que concierge pour ou au nom d'un client,
          vous acceptez les présentes modalités et nos{' '}
          {conciergeTermsLink('modalités relatives aux concierges')}. Le non-respect des modalités
          relatives aux concierges constitue une violation des présentes modalités.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>2. Créer un ou des comptes</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>2.1. S'inscrire.</b> Pour utiliser nombre des services, vous devez d'abord créer un
            compte (« compte »). Vous consentez à nous fournir des renseignements exacts, complets
            et toujours actualisés pour votre compte. Nous pouvons être amenés à utiliser ces
            renseignements pour communiquer avec vous.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>2.2. Rester en sécurité.</b> Veuillez protéger votre compte et vous assurer que
            d'autres personnes n'ont pas accès à votre compte ou à vos mots de passe et autres
            renseignements d'authentification (collectivement, les « mots de passe »). Vous êtes
            exclusivement responsable de toutes les activités sur votre compte et du maintien de la
            confidentialité et de la sécurité de vos mots de passe. Nous ne sommes pas responsables
            des actes ou omissions de votre part ou de celle de toute autre personne en rapport avec
            votre compte. Vous devez nous informer immédiatement par courriel au{' '}
            {privacyEmailAnchor()} si vous savez ou avez des raisons de soupçonner que votre compte
            ou vos mots de passe ont été volés, détournés ou compromis d'une autre manière, ou en
            cas d'utilisation non autorisée réelle ou suspectée de votre compte.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>2.3. Admissibilité, suspension et résiliation.</b> Les services ne sont pas destinés
            aux personnes de moins de 18 ans et ne peuvent pas être utilisés par celles-ci. En
            utilisant les services, vous déclarez avoir au moins 18 ans. Nous pouvons, à notre
            discrétion raisonnable, refuser d'offrir les services à quiconque, personne ou entité, y
            compris annuler une réservation ou une transaction, lorsque la loi nous y oblige ou si
            nous avons résilié votre compte. Nous nous réservons le droit de suspendre ou de
            résilier les comptes contenant des renseignements intentionnellement inexacts. Nous nous
            réservons le droit de résilier tous les comptes appartenant à un utilisateur qui
            contrevient aux présentes modalités d'utilisation.
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>3. Réservations</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>3.1. Réservations.</b> Les services vous permettent d'effectuer des réservations
            auprès des marchands (chacune étant une « réservation »). Les réservations peuvent
            consister en : a) la réservation d'une expérience auprès d'un marchand (chacune étant
            une « expérience de marchand »); b) la passation d'une commande pour emporter ou de
            livraison auprès d'un marchand; c) l'inscription sur une liste d'attente pour une
            expérience de marchand auprès d'un marchand; d) la passation ou le paiement d'une
            commande auprès d'un marchand en personne; ou e) tout autre type de réservation ou
            d'achat mis à votre disposition par l'intermédiaire des services. Pour effectuer une
            réservation auprès d'un marchand, allez à une page concernant le marchand sur les
            services (chacune étant une « page de marchand »), sélectionnez l'expérience souhaitée,
            passez une commande ou inscrivez-vous sur une liste d'attente, puis suivez les
            instructions pour effectuer votre réservation. Lorsque vous effectuez une réservation,
            nous vous envoyons une confirmation par courriel comprenant un numéro de confirmation
            unique. Un marchand peut vous demander de présenter votre numéro de confirmation et la
            carte de crédit que vous avez utilisée pour effectuer votre réservation. Si votre
            réservation comprend de l'alcool, vous devez également présenter une pièce d'identité
            valide qui montre que vous avez au moins l'âge légal pour consommer de l'alcool dans
            l'endroit concerné, comme l'exigent les lois en vigueur dans cet endroit.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>3.2. Prix des marchands, disponibilité et modalités.</b> Chaque marchand (et non
            Tock) est seul responsable de sa page de marchand, y compris mais sans s'y limiter, du
            prix, de la disponibilité et d'autres détails concernant toutes les réservations
            inscrites par ce marchand ou en son nom. Ces détails peuvent figurer sur une page de
            marchand et s'appliquer à toutes les réservations auprès d'un marchand (« modalités
            générales du marchand ») ou s'appliquer à une réservation particulière et figurer dans
            les renseignements ou explications se rapportant à cette réservation (« modalités
            particulières de réservation du marchand »). Les modalités générales du marchand et (ou)
            les modalités particulières de réservation du marchand peuvent comprendre des règles et
            des restrictions supplémentaires liées à un marchand ou à une réservation particulière,
            y compris : a) des détails sur la politique d'annulation, de remboursement et (ou)
            d'échange du marchand; b) des limitations du nombre de places assises du marchand; c)
            des limitations concernant l'expérience du marchand ou la quantité de commandes d'une
            autre réservation; d) des limitations de la capacité du marchand à répondre à toute
            restriction alimentaire ou autre liée à une réservation. Les marchands peuvent modifier
            le prix et la disponibilité de toute réservation à tout moment, avec ou sans préavis, y
            compris en ajoutant ou en supprimant des créneaux de réservation. Les marchands peuvent
            également modifier à tout moment les modalités générales du marchand ou les modalités
            particulières de réservation du marchand. Il vous incombe de vous assurer d'avoir lu les
            modalités générales du marchand ou les modalités particulières de réservation du
            marchand en vigueur au moment où vous effectuez votre réservation. Avant d'effectuer une
            réservation, il vous incombe de prendre connaissance des modalités générales du marchand
            et des modalités particulières de réservation en vigueur du marchand en question. Par
            exemple, les modalités générales du marchand et (ou) les modalités particulières de
            réservation du marchand peuvent indiquer que : i) certaines réservations sont
            définitives, non remboursables, non transférables et (ou) non échangeables; ii) si vous
            n'arrivez pas à l'heure, ou ne vous présentez pas du tout à l'établissement du marchand,
            ou si vous ne donnez pas de préavis d'annulation d'une réservation conformément à la
            politique d'annulation du marchand, vous pourriez être soumis à des frais de client
            défaillant ou d'annulation tardive qui seront facturés à la méthode de paiement utilisée
            pour effectuer cette réservation. En effectuant une réservation, vous acceptez toutes
            les modalités générales du marchand ou les modalités particulières de réservation du
            marchand qui s'appliquent à votre réservation. Tock n'est en aucun cas responsable des
            frais facturés à votre méthode de paiement en cas de manquement à l'obligation d'annuler
            votre réservation ou de se conformer aux modalités générales du marchand ou aux
            modalités particulières de réservation du marchand.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>3.3. Transférabilité des réservations.</b> Votre réservation peut être transférable.
            Vous déclarez et garantissez que votre transfert de toute réservation sera conforme aux
            lois applicables dans votre territoire. Si vous achetez une réservation auprès d'une
            entité autre que Tock, vous le faites à vos propres risques. Tock n'est pas responsable
            des contrefaçons ou des fausses déclarations. La revente illégale (ou la tentative de
            revente illégale), la contrefaçon ou la copie d'une réservation est un motif de saisie
            et d'annulation de cette réservation et de toute autre réservation que vous auriez
            effectuée, sans contrepartie. En outre, Tock se réserve le droit de limiter ou de
            refuser les privilèges d'achat de la réservation à toute personne qui, selon Tock,
            enfreint ou a enfreint les présentes modalités. Comme Tock ne garantit pas
            l'authenticité des réservations achetées auprès d'autres entités qu'elle-même (y compris
            mais sans s'y limiter, auprès de courtiers ou d'autres utilisateurs de Tock), Tock vous
            recommande de n'acheter des réservations que directement auprès d'elle. Les réservations
            ne peuvent être utilisées à des fins de publicité, de promotion, de concours ou de
            loterie sans l'accord écrit préalable d'un représentant autorisé de Tock et du marchand
            concerné.
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>4. Marchands</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>4.1. Responsabilités du marchand.</b> Vous reconnaissez et comprenez par la présente
            que chaque marchand (et non Tock) est seul responsable de tous les aspects de ses
            affaires et activités, y compris la fourniture de nourriture, de boissons et d'autres
            aspects d'une réservation, du service, de l'environnement, de la sécurité, de la qualité
            et de l'exactitude.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>4.2. Conformité du marchand.</b> Vous reconnaissez et comprenez par les présentes que
            chaque marchand (et non Tock) est entièrement responsable de la commercialisation, de la
            vente, de l'établissement des prix, de l'emballage et de la fourniture de tous les
            produits, services ou autres réservations offerts aux clients par l'entremise des
            services, conformément à toutes les lois applicables. Tock ne fait aucune déclaration et
            ne donne aucune garantie quant au fait qu'un marchand détient un permis, une licence, un
            enregistrement ou une autre accréditation pour son entreprise, que les déclarations d'un
            marchand sont vraies et exactes ou qu'un marchand se conforme aux lois applicables, et
            Tock n'est pas responsable de la qualité des produits ou des services fournis par les
            marchands.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>4.3. Exonération de responsabilité.</b> Les marchands sont seuls responsables de tous
            les dommages, pertes, responsabilités, coûts, réclamations, blessures, demandes,
            amendes, récompenses et dépenses de toute nature (y compris mais sans s'y limiter, les
            frais et honoraires raisonnables d'avocat) (collectivement, les « pertes ») que vous
            avez subis à la suite de votre interaction avec un marchand ou de votre visite chez un
            marchand, ou à la suite d'une réservation, d'une promotion, d'une offre, d'un produit ou
            d'un service d'un marchand. Vous devez régler toutes les contestations directement
            auprès du marchand. Dans toute la mesure permise par la loi applicable, vous exonérez
            par la présente Tock, ses sociétés affiliées et ses dirigeants, administrateurs,
            employés et agents respectifs (collectivement, les « parties de Tock ») de toutes ces
            pertes. DANS LE CADRE DE CE QUI PRÉCÈDE, SI VOUS RÉSIDEZ EN CALIFORNIE, VOUS RENONCEZ
            PAR LA PRÉSENTE À L'ARTICLE 1542 DU CODE CIVIL CALIFORNIEN, QUI STIPULE CE QUI SUIT :
            LES EXONÉRATIONS GÉNÉRALES NE S'ÉTENDENT PAS AUX CRÉANCES QUE LE CRÉANCIER NE CONNAÎT
            PAS OU DONT IL NE SOUPÇONNE PAS L'EXISTENCE EN SA FAVEUR AU MOMENT OÙ IL SIGNE
            L'EXONÉRATION ET QUI, S'IL LES AVAIT CONNUES, AURAIENT AFFECTÉ DE MANIÈRE SIGNIFICATIVE
            SON RÈGLEMENT AVEC LE DÉBITEUR. Par la présente, vous renoncez expressément à tous les
            droits et avantages en vertu du présent article et de toute loi de tout endroit ayant un
            effet similaire en ce qui concerne l'exonération de toute réclamation inconnue ou
            insoupçonnée que vous pourriez avoir contre les parties de Tock concernant l'objet du
            présent article 4.3. Si vous résidez au Royaume-Uni ou dans l'Union européenne, la
            présente exonération ne s'applique pas aux dommages résultant d'une faute intentionnelle
            ou d'une négligence grave de notre part ni aux dommages résultant d'une atteinte à la
            vie, à l'intégrité physique ou à la santé.
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>5. Réservations payées</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>5.1. Réservations payées.</b> Si vous souhaitez effectuer une réservation nécessitant
            le versement d'une somme d'argent à un marchand ou nécessitant une carte de débit ou de
            crédit pour garantir cette réservation (chacune étant une « réservation payée »), Tock
            ou un tiers chargé du traitement des paiements vous demandera de fournir des
            renseignements de paiement (« renseignements de paiement »). Vous convenez que tous les
            renseignements de paiement que vous fournissez à Tock ou à ce tiers sont exacts, actuels
            et complets. Vous acceptez de régler tous les frais de réservation payée encourus par
            vous ou par tout utilisateur de votre compte et (ou) de votre carte de crédit (ou de
            tout autre mécanisme de paiement) aux prix en vigueur au moment de l'achat ou tels que
            vous en avez convenu, y compris les frais du marchand, les frais de Tock et les taxes
            applicables (tels que définis ci-dessous). « Frais du marchand » désigne l'ensemble des
            frais de service ou autres frais liés à une réservation payée donnée, qui sont définis
            et seront perçus par le marchand. « Frais de Tock » désigne tous les frais de service ou
            autres frais relatifs à une réservation payée donnée qui seront perçus par Tock. Les
            frais de Tock ne sont pas remboursables. Les frais du marchand et les frais de Tock sont
            indiqués comme des articles distincts lors de l'achat. Tock accepte plusieurs méthodes
            de paiement possibles pour vous permettre d'effectuer une réservation payée. Vous
            déclarez et garantissez que vous avez légalement le droit d'utiliser toute carte de
            crédit ou tout autre mécanisme de paiement utilisé dans le cadre d'une réservation
            payée. En soumettant ces renseignements de paiement, vous accordez à Tock le droit de
            les fournir à un tiers dans le but de faciliter l'exécution de votre réservation payée.
            La vérification des renseignements de paiement peut être exigée avant l'exécution de
            toute réservation payée.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>5.2. Tock comme agent de recouvrement.</b> Les paiements que vous effectuez auprès
            d'un marchand par l'intermédiaire des services peuvent être soumis à Tock et, dans ce
            cas, Tock agira en tant qu'agent de recouvrement de ce marchand dans le seul but
            d'accepter et de traiter les paiements que vous effectuez. La remise de votre paiement à
            Tock (agissant en tant qu'agent de recouvrement du marchand concerné) satisfait à vos
            obligations envers le marchand. Une fois le paiement reçu par Tock pour le marchand, ce
            dernier a autorisé Tock à créditer électroniquement les fonds dans un compte bancaire
            qu'il lui a indiqué. Dans l'éventualité où Tock ne verserait pas ces montants, ce
            marchand n'aura de recours que contre Tock et non contre vous directement.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>5.3. Facilitateur de marché et taxes.</b> Tock peut être qualifiée de « facilitatrice
            de marché » dans certains endroits. Une entreprise facilitatrice de marché est
            généralement définie dans les lois des États fédérés des États-Unis comme une entreprise
            qui passe des contrats avec des vendeurs tiers pour promouvoir la vente de biens
            physiques, de biens numériques et (ou) de services détenus et fournis par un tiers par
            l'intermédiaire d'un site Web en ligne. Pour autant que Tock est une facilitatrice de
            marché, elle est responsable du calcul, de la collecte et du versement des taxes de
            vente et d'utilisation. Tock facturera et percevra les taxes de vente et d'utilisation
            sur les transactions imposables, et vous serez responsable du paiement de ces taxes de
            vente et d'utilisation si elles sont facturées. Tous les frais relatifs aux réservations
            payées (y compris les frais du marchand et les frais de Tock applicables) ne comprennent
            pas les taxes nationales, provinciales, d'État, locales ou autres (« taxes »), sauf
            indication contraire explicite.
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>6. Protection des renseignements personnels</b>
        </Typography>
        <Typography variant="body02" paragraph mb={2}>
          En utilisant les services, vous confirmez que vous avez lu et compris la{' '}
          {privacyPolicyLink('politique de protection des renseignements personnels')}. Votre accès
          aux services et votre utilisation de ceux-ci sont régis par la politique sur la protection
          des renseignements personnels de Tock, laquelle est incorporée par renvoi dans les
          présentes modalités.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>7. Pages des services de tiers et de marchands</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>7.1. Services de tiers.</b> Les services (y compris les pages des marchands) peuvent
            être liés ou intégrés à divers sites Web, services ou applications de tiers
            (collectivement, les « services de tiers ») qui sont susceptibles de mettre leur
            contenu, leurs produits ou leurs services à votre disposition. Les pages des marchands
            comprennent souvent des liens vers des services de tiers liés à ce marchand en
            particulier. Parmi les exemples de services de tiers figurent les articles sur un
            marchand ou son site Web; et les articles sur l'industrie du tourisme réceptif, et les
            entreprises de traitement des paiements. Si vous cliquez sur un lien vers un service
            tiers, il se peut que vous quittiez les services et que vous alliez au service tiers que
            vous avez sélectionné. Ces services de tiers peuvent avoir leurs propres modalités et
            politiques, et l'utilisation que vous en ferez sera régie par ces modalités et
            politiques.{' '}
            <b>
              Nous ne contrôlons pas les services de tiers et n'en sommes pas responsables. Nous ne
              sommes pas non plus responsables pour toute transaction que vous pourriez effectuer
              avec eux ou de ce qu'ils font.
            </b>
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>7.2. Contenu.</b> Les services (y compris les pages des marchands) peuvent renfermer
            du contenu, y compris mais sans s'y limiter du texte, des photos, des images, de la
            musique, des sons, des vidéos, des logos et du matériel associé (« contenu ») d'un
            marchand (« contenu du marchand ») et (ou) des expériences potentielles des marchands :
            a) qui sont offensants ou répréhensibles; b) qui comprennent des erreurs; c) dont le
            téléchargement, la copie ou l'utilisation sont soumis à des modalités et politiques
            supplémentaires de tiers ou sont protégés par des lois sur la propriété intellectuelle,
            le secret commercial, le respect de la vie privée ou d'autres lois. Tock n'est pas
            responsable du contenu ou de l'expérience des marchands.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>7.3. Pas de promotion.</b> Les descriptions ou images de services de tiers ou
            d'expériences de marchands, ou les références à ces services ou expériences de marchands
            à partir des services ne signifient pas que Tock en fait la promotion. Tock ne s'occupe
            ni de la prestation ni de la promotion des services de tiers ou de l'expérience du
            marchand, et elle n'en fait pas la garantie. Les marchands sont des tiers; Tock n'est
            pas affiliée à un marchand et n'est pas non plus responsable du contenu du marchand ou
            de ses expériences. En exploitant les services, nous ne déclarons ni ne sous-entendons
            que nous approuvons le contenu du marchand ou ses expériences, ou encore que nous
            estimons que ce contenu ou ces expériences (ou toute description ou image de ceux-ci, ou
            toute référence à ceux-ci) sont exacts, complets, fiables, actuels, légaux ou non
            préjudiciables. Nous ne sommes pas éditeurs de contenus de marchand téléchargés,
            affichés, publiés ou mis à disposition d'une autre manière sur une page de marchand, et
            nous ne sommes pas non plus responsables de ces contenus.
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>8. Propriété intellectuelle et restrictions sur les services</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>8.1. Utilisation des services.</b> Nous vous autorisons à utiliser les services
            uniquement comme indiqué dans les présentes modalités. Sans le consentement écrit
            préalable d'un représentant dûment autorisé de Tock, vous acceptez de ne pas : a) copier
            ou distribuer toute partie des services sur quelque support que ce soit; ii) transformer
            ou modifier toute partie des services, sauf dans la mesure où cela est raisonnablement
            nécessaire pour utiliser les services aux fins prévues; c) démanteler, décompiler ou
            faire de l'ingénierie inverse sur toute partie des services.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>8.2. Propriété des services.</b> À l'exception du contenu marchand, les services sont
            la propriété de Tock et sont protégés par des droits d'auteur, des secrets commerciaux,
            des marques commerciales et d'autres lois américaines et étrangères. Les présentes
            modalités ne vous confèrent aucun droit, titre ou intérêt sur les services, le contenu
            des marchands, les marques, les logos ou les autres contenus de Tock, les
            caractéristiques de la marque, la propriété intellectuelle ou les secrets commerciaux.
            Vous acceptez de ne pas changer, modifier, traduire ou créer des œuvres dérivées des
            services ou de leur contenu (y compris le contenu du marchand).
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>8.3. Contenu de l'utilisateur.</b> Les services peuvent vous permettre de soumettre,
            de transmettre, d'afficher ou de fournir d'une autre manière du contenu, y compris mais
            sans s'y limiter, des critiques, des commentaires, des évaluations, des photos, des
            images, des vidéos, des sons, des textes, des données, des liens et des renseignements
            de positionnement (« contenu de l'utilisateur »). Ce contenu de l'utilisateur ne doit
            pas : a) être illégal, menaçant, obscène, raciste, diffamatoire, pornographique;
            enfreindre des droits de propriété intellectuelle; promouvoir des activités illégales ou
            porter préjudice à des groupes et (ou) des personnes; porter atteinte aux droits d'un
            tiers en matière de publicité ou de protection des renseignements personnels; être
            délibérément faux ou autrement préjudiciable à des tiers, ou être répréhensible; b)
            contenir ou consister en des logiciels, des virus informatiques, des sollicitations
            commerciales, des campagnes politiques, des chaînes de lettres, du publipostage, toute
            forme de « pourriel » ou des références à des activités illégales, à des fautes
            professionnelles, à des surfacturations intentionnelles, à de la publicité mensongère ou
            à des violations du code de la santé (par exemple, présence de corps étrangers dans les
            aliments, intoxication alimentaire, etc.) Votre contenu d'utilisateur doit être
            impartial et objectif. Vous ne pouvez pas soumettre d'avis, de commentaires ou
            d'évaluations pour lesquels vous êtes rémunéré de quelque manière que ce soit, ou pour
            votre propre restaurant ou celui de votre employeur, d'un ami, d'un parent ou d'un
            concurrent. Vous ne pouvez pas utiliser une fausse adresse de courriel, usurper
            l'identité d'une personne ou d'une entité, ou tromper quant à l'origine du contenu de
            l'utilisateur. Le nom ou le pseudonyme que vous choisissez de fournir à Tock peut être
            affiché publiquement avec ce contenu d'utilisateur. Tock se réserve le droit (mais n'a
            aucune obligation) de surveiller, de supprimer ou de modifier le contenu d'utilisateur à
            sa seule discrétion, y compris si ce contenu enfreint les présentes modalités, mais vous
            reconnaissez que Tock peut ne pas examiner régulièrement le contenu d'utilisateur
            soumis. Si vous soumettez du contenu d'utilisateur, et sauf indication contraire de
            notre part, vous accordez à Tock le droit non exclusif, perpétuel, libre de redevances,
            irrévocable et entièrement sous-licenciable (à plusieurs niveaux, y compris aux
            restaurants, partenaires et autres sites Web et flux de tiers) d'utiliser, de modifier,
            de reproduire, d'adapter, de traduire, de publier, de créer des œuvres dérivées, de
            distribuer, d'afficher et d'exploiter de toute autre manière ce contenu d'utilisateur
            dans le monde entier, le cas échéant. Vous renoncez par les présentes à tout droit à la
            protection des renseignements personnels ou à la publicité ou à tout droit similaire sur
            le nom ou l'image d'une personne, en plus de tout droit moral ou autre droit que vous
            pourriez avoir sur le contenu d'utilisateur que vous soumettez au profit de Tock. Vous
            déclarez que vous êtes propriétaire ou que vous disposez des autorisations nécessaires
            pour utiliser et autoriser l'utilisation du contenu d'utilisateur tel que décrit dans le
            présent document. Tock n'assume aucune responsabilité au regard de tout contenu
            d'utilisateur que vous ou tout autre utilisateur ou tiers avez soumis, et ne garantit
            aucunement la confidentialité en ce qui concerne le contenu d'utilisateur.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>8.4 Interdiction de scruter les services.</b> Les robots Web et toutes les
            technologies similaires sont strictement interdits. Vous acceptez de ne pas utiliser et
            de ne pas fournir de logiciels (à l'exception des navigateurs Web et des clients de
            messagerie à usage général, ou des logiciels expressément autorisés par Tock) ou de
            services qui contournent nos services, ou interagissent ou fonctionnent de concert avec
            eux d'une manière qui est interdite par les présentes modalités. Votre utilisation, ou
            celle de toute personne qu'y en aurait reçu l'autorisation de votre part, de machines,
            d'ordinateurs, de scripts ou de tout système automatisé sur le site est strictement
            interdite et peut engager votre responsabilité pénale en vertu de la{' '}
            <i>Computer Fraud and Abuse Act</i>, de l'article 1030 du U.S. Code et (ou) en vertu des
            lois applicables dans d'autres territoires.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>8.5 Rétroaction.</b> Nous accueillons volontiers vos commentaires, idées ou
            suggestions (collectivement, les « rétroactions »), mais vous acceptez que nous
            puissions utiliser vos commentaires sans aucune restriction ni obligation à votre égard,
            même après la résiliation des présentes modalités. Le présent article ne limite pas les
            droits que vous pouvez avoir en vertu des lois applicables en matière de protection des
            données ni n'a d'incidence sur ces droits.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>8.6 Autres restrictions concernant les services.</b> Vous acceptez de ne pas : a)
            utiliser les services à des fins non autorisées, y compris la collecte ou la récolte de
            renseignements sur Tock, nos marchands ou nos clients, par des moyens électroniques ou
            autres, à quelque fin que ce soit, y compris mais sans s'y limiter, pour envoyer des
            courriels non sollicités ou d'autres communications électroniques, ou vous lancer dans
            un cadrage non autorisé des services ou des liens vers les services sans notre
            consentement écrit exprès; b) utiliser les services de manière à tenter de : i) vous
            faire passer pour une autre personne ou entité; ii) vendre ou laisser d'autres personnes
            utiliser votre profil ou mot de passe; iii) fournir une identification fausse ou
            trompeuse ou des renseignements du même ordre; iv) porter atteinte à la vie privée ou
            violer les droits personnels ou de propriété de toute personne ou entité; c) utiliser
            les services d'une manière qui pourrait empêcher d'autres clients d'utiliser les
            services ou d'en profiter, ou encore d'effectuer une réservation. En outre, vous
            déclarez et garantissez que votre utilisation des services est conforme aux lois
            applicables, y compris mais sans s'y limiter, aux contrôles et réglementations
            applicables en matière d'exportation ou d'importation et aux sanctions.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <Typography mb={2}>
              <b>8.7 Plaintes liées au droit d'auteur (Digital Millennium Copyright Act).</b> En
              exploitant les services, nous pouvons agir comme fournisseur de service, selon la
              définition donnée dans la Digital Millennium Copyright Act (« DMCA ») pour « Service
              provider », et offrir des services en tant que fournisseur en ligne de matériel et de
              liens vers des sites Web de tiers. Il est donc possible que du matériel de tiers ne
              nous appartenant pas ou que nous ne contrôlons pas soit transmis, conservé, consulté
              ou rendu disponible d’une autre manière à l’aide des services. Tock a en place
              certaines procédures exigées par la loi en cas d’allégation de violation de droit
              d’auteur liée aux services. Si vous croyez que du matériel accessible par
              l’intermédiaire des services viole un droit d’auteur, vous devez nous en aviser en
              suivant la procédure d’avis de violation présumée de la DMCA (17 U.S.C. sect.
              512(c)(2)). Nous retirerons promptement tout matériel signalé comme une violation ou
              en désactiverons l’accès, et suivrons la procédure de la DMCA pour régler le différend
              entre la personne ayant signalé le matériel en question et la personne accusée qui a
              fourni le matériel. Notre agent désigné (c’est-à-dire la bonne personne à aviser),
              celui à qui vous devez signaler toute violation soupçonnée de la DMCA, est le
              conseiller en propriété intellectuelle d’American Express (
              <Anchor href="mailto:DMCA@aexp.com">DMCA@aexp.com</Anchor>).
            </Typography>
            <Typography>Le signalement doit comprendre :</Typography>
            <Typography component="ul">
              <Typography component="li" css={sectionListItem}>
                1. l’identification de l’œuvre ou autre propriété intellectuelle protégée par droit
                d’auteur faisant l’objet d’une violation selon vous;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                2. l’identification du matériel trouvé dans les services qui représente une
                violation selon vous, et suffisamment de détails pour que nous puissions le trouver
                dans les services;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                3. une déclaration de votre part que vous croyez de bonne foi que l’utilisation
                faite n’est pas autorisée par le propriétaire du droit d’auteur, son représentant ou
                la loi;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                4. une déclaration de votre part, sous peine de parjure, que a) les renseignements
                susmentionnés contenus dans votre signalement sont exacts; et que b) vous êtes
                propriétaire du matériel protégé par droit d’auteur en question ou êtes autorisé à
                agir au nom du propriétaire dudit matériel;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                5. vos adresse, numéro de téléphone et adresse de courriel;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                6. votre signature manuscrite ou électronique.
              </Typography>
            </Typography>
            <Typography>
              Nous pouvons informer nos utilisateurs des signalements de violation soupçonnée en
              publiant un avis général sur les services, en envoyant un courriel à l’adresse de
              courriel de l’utilisateur figurant dans nos dossiers ou en envoyant une communication
              par courrier de première classe à l’adresse réelle de l’utilisateur figurant dans nos
              dossiers. Si vous recevez un tel avis de violation présumée, vous pouvez envoyer à
              l’agent désigné une notification de contestation par écrit comprenant les
              renseignements ci-dessous. Pour être valide, la notification de contestation doit être
              une communication écrite comprenant :
            </Typography>
            <Typography component="ul">
              <Typography component="li" css={sectionListItem}>
                1. votre signature manuscrite ou électronique;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                2. l’identification du matériel ayant été retiré ou dont l’accès a été désactivé, et
                l’endroit où ce matériel se trouvait avant d’être retiré ou avant que l’accès n’en
                soit désactivé;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                3. une déclaration de votre part, sous peine de parjure, que vous croyez de bonne
                foi que le matériel a été retiré ou que l’accès en a été désactivé en raison d’une
                erreur ou d’un malentendu concernant ledit matériel;
              </Typography>
              <Typography component="li" css={sectionListItem}>
                4. vos nom, adresse réelle et numéro de téléphone, et une déclaration que vous
                consentez à être soumis à l’autorité du territoire d’une Cour de district fédérale
                relevant du district judiciaire dans lequel se trouve votre adresse réelle ou, si
                votre adresse réelle se trouve à l’extérieur des États-Unis, de tout district
                judiciaire dans lequel nous pouvons nous trouver, et que vous accepterez la
                signification d’un acte de procédure de la part de la personne ayant soumis l’avis
                de violation présumée ou d’un représentant de ladite personne.
              </Typography>
            </Typography>
            <p>La DMCA ne s’applique qu’aux États-Unis.</p>
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>9. Mesures importantes que nous pouvons prendre.</b> Nous nous réservons les droits
          suivants, que nous pouvons exercer à tout moment et à notre seule discrétion, sans
          responsabilité ni préavis (sauf si la loi applicable l'interdit) : a) nous pouvons
          modifier tout ou partie des services et de leurs fonctionnalités; b) nous pouvons
          suspendre ou interrompre l'ensemble ou une partie des services; c) nous pouvons résilier,
          suspendre, restreindre ou désactiver votre accès ou votre utilisation de l'ensemble ou
          d'une partie des services; d) nous pouvons résilier, suspendre, restreindre ou désactiver
          l'accès à vos comptes ou à l'ensemble ou à une partie de vos réservations; e) nous pouvons
          honorer tout coupon, code de réduction, code promotionnel ou autres promotions similaires
          ou imposer des conditions à leur reconnaissance; f) nous pouvons interdire à un compte
          d'effectuer une ou plusieurs réservations; g) nous pouvons refuser de permettre à un
          compte d'effectuer une réservation en particulier; h) nous pouvons modifier nos critères
          d'admissibilité pour l'utilisation des services (et si ces modifications des critères
          d'admissibilité sont interdites par la loi dans votre pays de résidence, nous pouvons
          révoquer votre droit d'utiliser les services dans ce territoire).
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>10. Durée et résiliation.</b> La présente convention restera en vigueur jusqu'à ce que
          vous la résiliez ou que nous la résilions; et vous acceptez que Tock ne soit pas
          responsable envers vous ou un tiers de cette résiliation. Vous pouvez résilier la présente
          convention à tout moment par l'intermédiaire des services. Nous nous réservons le droit de
          modifier, de suspendre ou d'interrompre, ou d'arrêter, de restreindre ou de désactiver
          votre utilisation ou votre accès à l'ensemble ou à une partie des services ou de leurs
          fonctionnalités, à tout moment, à notre seule discrétion et sans préavis. Par exemple,
          nous pouvons suspendre ou mettre fin à votre utilisation d'une partie ou de la totalité
          des services si vous enfreignez les présentes modalités. Nous nous efforcerons de vous
          donner un préavis raisonnable en cas de suspension ou de résiliation d'une partie ou de la
          totalité des services. Nous nous réservons également le droit de retirer tout marchand des
          services à tout moment et à notre seule discrétion. Dans le cas improbable d'une telle
          suppression, nous pourrions être amenés à annuler les réservations que vous avez
          effectuées auprès de ces marchands. Nous vous informerons de ces annulations. Tous les
          articles de la présente convention qui, du fait de leur nature, doivent survivre à la
          résiliation, survivent à la résiliation, y compris mais sans s'y limiter, les articles
          suivants des présentes modalités et tout article ou toute disposition similaire dans le
          reste de la présente convention : Réservations, Marchands, Réservations payées,
          Exonération de responsabilité, Dénis de garantie, Limitation de responsabilité,
          Indemnisation, Règlement des contestations et Modalités supplémentaires.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>11. Dénis de garantie.</b> Dans toute la mesure permise par la loi applicable, Tock ne
          donne aucune garantie, expresse ou implicite, concernant les services, tout contenu du
          marchand ou contenu de l'utilisateur sur ceux-ci ou tout marchand. Les services et
          l'ensemble du contenu du marchand ou du contenu de l'utilisateur qui s'y trouvent sont
          fournis « en l'état » et « selon leur disponibilité ». Tock décline également toute
          garantie de qualité marchande, d'adéquation à un usage particulier et d'absence de
          contrefaçon. Aucun conseil ni renseignement, qu'il soit oral ou écrit, que vous auriez
          obtenu auprès de Tock, ne peut donner lieu à une quelconque garantie. Tock ne fait aucune
          déclaration et ne donne aucune garantie : a) au sujet de l'exactitude ou de l'exhaustivité
          des services ou de tout contenu du marchand ou contenu de l'utilisateur qui s'y trouve; b)
          que les services ou tout contenu du marchand ou contenu de l'utilisateur qui s'y trouvent
          : i) seront opportuns, ininterrompus ou exempts d'erreurs; ii) répondront à vos exigences
          ou à vos attentes; iii) seront exempts de virus ou d'autres éléments nuisibles.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>12. Limitation de responsabilité.</b> Vous reconnaissez et acceptez que, dans toute la
          mesure permise par la loi applicable, les parties de Tock ne seront en aucun cas
          responsables des réclamations découlant des services ou de la présente convention, ou s'y
          rapportant, pour ce qui suit : a) tout dommage indirect, spécial, accessoire, exemplaire,
          punitif ou consécutif; b) toute perte de profits, revenus, données, fonds de commerce ou
          autres pertes intangibles; c) toute perte (telle que définie ci-dessous) liée à votre
          accès, utilisation ou incapacité d'accès ou d'utilisation de l'intégralité ou d'une partie
          de votre compte, de vos réservations ou de l'intégralité ou d'une partie des services, y
          compris mais sans s'y limiter, l'interruption de l'utilisation ou la cessation ou la
          modification de tout aspect des services; d) toute perte liée à l'indisponibilité, la
          dégradation, la perte, la corruption, le vol, l'accès non autorisé ou la transformation
          non autorisée de tout contenu, tout renseignement ou toute donnée; e) tout contenu du
          marchand ou autre conduite ou contenu de tout utilisateur ou tiers utilisant les services,
          y compris mais sans s'y limiter, toute conduite ou tout contenu diffamatoire, offensant ou
          illégal; f) tout service de tiers ou site de tiers accessible par l'intermédiaire des
          services. Ces limitations s'appliquent à toute théorie de responsabilité, qu'elle soit
          basée sur la garantie, le contrat, le délit, la négligence, la responsabilité stricte ou
          toute autre théorie juridique, que Tock ait été informée ou non de la possibilité de tels
          dommages, et même s'il s'avère qu'un recours énoncé dans la présente convention n'a pas
          atteint son objectif essentiel. Dans toute la mesure permise par la loi applicable, la
          responsabilité globale de Tock au regard de toutes les réclamations découlant des services
          et de la présente convention ou s'y rapportant ne peut en aucun cas dépasser le montant le
          plus élevé entre vingt dollars (20 $) et les montants que vous avez payés à Tock, que ce
          soit directement ou en agissant en tant qu'agent de recouvrement au nom d'un ou de
          plusieurs marchands, au cours des trois (3) mois précédant immédiatement l'événement qui a
          donné lieu à une telle réclamation.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>13. Indemnisation.</b> Dans toute la mesure permise par la loi, vous acceptez
          d'indemniser et de dégager de toute responsabilité Tock et ses sociétés affiliées, ainsi
          que leurs administrateurs, dirigeants, employés et agents, contre toutes les pertes
          découlant de ou liées à : a) votre violation des présentes modalités; b) votre utilisation
          et votre accès aux services ou à leur contenu; c) votre violation d'une loi ou d'un
          règlement ou des droits ou de la réputation d'un tiers. Tock a le droit, mais non
          l'obligation, de participer à la défense de ces actions avec l'avocat de son choix. Vos
          obligations d'indemnisation en vertu du présent article ne s'appliquent pas dans la mesure
          où elles résultent directement de notre violation de la présente convention.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>14. Règlement des contestations</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" variant="body02" paragraph css={sectionListItem}>
            <b>14.1. Indemnisation.</b> Avant de déposer une réclamation contre Tock, vous acceptez
            de tenter de résoudre la contestation en envoyant un avis de votre réclamation à
            American Express ADR c/o CT Corporation System, 28 Liberty Street, New York, New York
            10005. Votre <u>avis de réclamation doit comprendre</u> une description de votre
            réclamation et une preuve de votre relation avec Tock. Si nous ne parvenons pas à régler
            la contestation dans les quatre-vingt-dix (90) jours suivant la réception de votre
            premier avis de réclamation, Tock ou vous pourrez alors entamer une procédure
            d'arbitrage.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>14.2. Convention d'arbitrage.</b>
            <br />
            <br />
            <Typography component="ul" paragraph mb={2}>
              <Typography component="li" css={sectionListItem}>
                <b>
                  14.2.1. À moins que vous ne choisissiez de vous retirer pendant la période de
                  retrait conformément à l'article 14.3, Tock et vous acceptez de résoudre toute
                  réclamation, question ou toute contestation découlant de la convention ou en
                  rapport avec les présentes modalités (y compris mais sans s'y limiter, son
                  existence, sa formation, son fonctionnement et sa résiliation) et (ou) les
                  services (y compris mais sans s'y limiter, les contestations et questions non
                  contractuelles) par le biais d'un arbitrage définitif et contraignant, et Tock et
                  vous renoncez expressément au droit à une procédure judiciaire officielle (y
                  compris mais sans s'y limiter, à un procès avec jury), à l'exception des cas
                  prévus dans le présent article. L'accès à la preuve et les droits de recours dans
                  le cadre de l'arbitrage sont généralement plus limités que dans le cadre d'un
                  procès, et d'autres droits dont vous et nous disposerions devant un tribunal
                  peuvent ne pas exister dans le cadre de l'arbitrage. L'arbitrage ne comprend ni
                  juge ni jury, mais seulement une tierce partie expérimentée et indépendante qui
                  agit en tant qu'arbitre; et la révision judiciaire d'une sentence arbitrale est
                  limitée.
                </b>
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>14.2.2.</b> L'arbitre est habilité à accorder toute mesure d'indemnisation prévue
                par la loi ou l'équité qui pourrait être accordée par un tribunal. L'arbitre a en
                outre le droit d'imposer des sanctions, conformément aux règles de l'American
                Arbitration Association (« AAA »), le cas échéant, y compris pour : a) toute
                réclamation ou soumission frivole dont l'arbitre estime qu'elle n'a pas été déposée
                de bonne foi; b) le non-respect de l'article 14 par une partie. Pour éviter toute
                ambiguïté, le droit d'imposer des sanctions comprend le droit de transférer les
                frais d'arbitrage si les règles de l'AAA le permettent.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>14.2.3.</b> Toute demande d'arbitrage ou demande reconventionnelle formulée par
                l'une ou l'autre partie doit contenir suffisamment de renseignements pour que
                l'autre partie soit dûment informée de l'identité de la partie requérante, des
                réclamations formulées et des allégations factuelles sur lesquelles reposent ces
                réclamations, et doit comporter la preuve que le requérant est partie aux présentes
                modalités. L'arbitre peut exiger la modification de toute demande ou demande
                reconventionnelle qui ne satisfait pas à ces exigences. L'arbitre a le droit
                d'imposer des sanctions pour toute réclamation qu'il juge frivole ou inadéquate (au
                titre de la norme énoncée dans la règle fédérale de procédure civile 11 et au titre
                de toutes normes similaires en vigueur dans d'autres territoires), y compris pour
                toute réclamation déposée au nom d'un demandeur qui n'est pas partie aux présentes
                modalités.
              </Typography>
            </Typography>
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>
              14.3. Non-participation à l'arbitrage. Vous pouvez refuser cette convention
              d'arbitrage en nous envoyant un avis de refus à l'adresse American Express, P.O. Box
              981556, El Paso, TX 79998 dans un délai de trente (30) jours à compter de la date à
              laquelle vous avez accepté les présentes modalités (« période de retrait »). Votre
              avis de refus doit comprendre votre nom complet, votre adresse résidentielle et une
              déclaration claire indiquant que vous ne souhaitez pas participer à l'arbitrage. Si
              vous renoncez à participer à l'arbitrage au titre de l'article 14.3, alors les
              articles 14.2, 14.4, 14.5 et 14.6 des présentes modalités ne s'appliqueront pas à
              vous. Cette renonciation n'a d'incidence sur aucun autre article des présentes
              modalités, y compris mais sans s'y limiter, les articles 14.8 (Délai de dépôt), 14.9
              (Pas de recours collectifs) et 15 (Droit applicable; forum judiciaire pour les
              contestations).
            </b>
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>14.4. Délai de dépôt pour l'arbitrage.</b> Tout arbitrage doit débuter par la
            présentation d'une demande d'arbitrage dans un délai d'un (1) an à compter de la date à
            laquelle la partie faisant valoir la réclamation a eu connaissance ou devrait
            raisonnablement avoir eu connaissance de l'acte, de l'omission ou du manquement à
            l'origine de la réclamation. Si la loi applicable interdit un délai de prescription d'un
            (1) an pour faire valoir des réclamations, toute réclamation doit être faite dans le
            délai le plus court autorisé par la loi applicable.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>14.5. Procédures d'arbitrage.</b> L'arbitrage sera géré par l'American Arbitration
            Association (« AAA ») (numéro sans frais : 1 800 778-7879, site Web :{' '}
            <Link href="https://www.adr.org/">www.adr.org</Link>) et réglé devant un arbitre unique.
            Si l'AAA ne peut pas effectuer l'arbitrage, Tock et vous conviendrez mutuellement d'un
            autre fournisseur d'arbitrage. Sous réserve des modifications apportées par le présent
            article 14, l'AAA administrera l'arbitrage conformément à ses règles et procédures de
            règlement des contestations en vigueur au moment du dépôt de la demande d'arbitrage (les
            « règles de l'AAA »), à l'exclusion de toute règle ou procédure régissant ou autorisant
            les actions collectives ou représentatives. Chaque partie est responsable de ses propres
            honoraires d'avocat, sauf dans la mesure où les règles de l'AAA, l'arbitre et (ou) la
            loi applicable en disposent autrement. Un jugement sur la décision rendue par cet
            arbitre peut être rendu par tout tribunal compétent. Le terme « arbitre », tel qu'il est
            utilisé à l'article 14, s'entend comme incluant l'AAA. Tock et vous pouvez convenir que
            : a) les présentes modalités ont une incidence sur le commerce entre les États fédérés,
            de sorte que la US Federal Arbitration Act et les lois fédérales sur l'arbitrage
            s'appliquent et régissent l'interprétation et l'application du présent article 14
            (malgré l'article 15 ci-dessous); b) toute audience d'arbitrage se déroule à New York
            (État de New York) dans un lieu dont il faut convenir, est administrée par l'AAA en
            anglais et est réglée par un (1) arbitre commercial possédant une expérience
            considérable dans le règlement de contestations concernant la propriété intellectuelle
            et les contrats commerciaux, qui sera sélectionné à partir de la liste appropriée des
            arbitres de l'AAA, conformément aux règles de cette dernière.
            <br />
            <br />
            <Typography component="ul" paragraph mb={2}>
              <Typography component="li" css={sectionListItem}>
                <b>14.5.1. Pas d'arbitrages collectifs ou joints sans consentement écrit.</b> Sauf
                dans la mesure où elle y consent à sa seule discrétion par écrit, Tock n'accepte ni
                ne consent en aucune circonstance à un arbitrage collectif, à un arbitrage général
                par un mandataire ou à un arbitrage impliquant des réclamations conjointes.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>14.5.2. Divisibilité des réclamations.</b> Si une décision judiciaire définitive
                empêche l'application des limitations du présent article 14 à une réclamation, un
                recours ou une demande de mesure d'indemnisation particulière, cette réclamation, ce
                recours ou cette mesure d'indemnisation (et uniquement cette réclamation, ce recours
                ou cette mesure d'indemnisation) doit être dissocié de l'arbitrage et peut faire
                l'objet d'une demande devant un tribunal. Les parties conviennent toutefois que
                toute décision relative à des réclamations, recours ou mesures d'indemnisation non
                soumises à l'arbitrage sera suspendue dans l'attente de l'issue des réclamations et
                recours arbitraux. Le présent article 14.5.2 ne vous empêche ni vous ni Tock de
                participer à un règlement collectif des réclamations.
              </Typography>
            </Typography>
          </Typography>
          <Typography>
            <b>14.6. Frais d'arbitrage.</b> Les règles de l'AAA régissent le paiement de tous les
            frais d'arbitrage. Nous ne demanderons pas les honoraires et frais de nos avocats dans
            le cadre de l'arbitrage, à moins que l'arbitre ne détermine que votre réclamation est
            frivole. Tock et vous convenez que l'AAA dispose du pouvoir discrétionnaire, si elle
            l'estime approprié, de réduire le montant ou de modifier le moment de paiement des frais
            administratifs ou d'arbitrage dus en vertu de ses règles applicables, à condition que
            cette modification n'augmente pas les coûts pour vous. Vous acceptez en outre de
            renoncer à toute objection à une telle modification des frais. Tock et vous convenez
            également qu'une contestation de bonne foi par l'une ou l'autre partie des frais imposés
            par l'AAA ne constitue pas un manquement, une renonciation ou une violation du présent
            article 14 tant que cette contestation reste en suspens auprès d'un arbitre et (ou) d'un
            tribunal compétent, et que toutes les dates d'exigibilité de ces frais sont suspendues
            pendant la durée de cette contestation.
          </Typography>
          <Typography>
            <b>14.7. Exceptions à la convention d'arbitrage.</b> Nonobstant toute disposition des
            présentes modalités, Tock ou vous pouvez intenter une action en justice uniquement pour
            obtenir une injonction visant à mettre fin à l'utilisation non autorisée ou à l'abus des
            services, ou à la violation ou à l'appropriation illicite de la propriété intellectuelle
            (par exemple, des marques de commerce, des secrets commerciaux, des droits d'auteur ou
            des droits de brevet) sans avoir recours à l'arbitrage ou à la procédure informelle de
            règlement des contestations décrite plus haut. Tock ou vous pouvez faire valoir vos
            droits, s'ils sont recevables, devant la Cour des petites créances de New York (État de
            New York) ou de tout autre comté américain où vous résidez ou travaillez.
          </Typography>
          <Typography>
            <b>14.8. Délai de dépôt.</b> Toute réclamation non soumise à l'arbitrage doit être
            introduite dans un délai d'un (1) an à compter de la date à laquelle la partie faisant
            valoir la réclamation a eu connaissance ou devrait raisonnablement avoir eu connaissance
            de l'acte, de l'omission ou du manquement à l'origine de la réclamation. Si la loi
            applicable interdit un délai de prescription d'un (1) an pour faire valoir des
            réclamations, toute réclamation doit être faite dans le délai le plus court autorisé par
            la loi applicable.
          </Typography>
          <Typography>
            <b>14.9. PAS DE RECOURS COLLECTIFS.</b> Vous ne pouvez régler les contestations auprès
            de nous qu'à titre individuel. Vous ne pouvez pas introduire une réclamation en tant que
            plaignant ou membre d'un recours collectif, dans le cadre d'un recours collectif, joint
            ou représentatif. Les recours collectifs, les arbitrages collectifs, les actions
            collectives, les actions générales de mandataire et le regroupement avec d'autres
            arbitrages ne sont pas autorisés, sauf dans la mesure où Tock, à sa seule discrétion, y
            consent par écrit conformément à l'article 14.5.1.
          </Typography>
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>15. Droit applicable; forum judiciaire pour les contestations.</b> Les présentes
          modalités (y compris leur existence, leur formation, leur fonctionnement et leur
          résiliation) et les services, ainsi que toutes les contestations et questions découlant
          des présentes modalités et des services ou s'y rapportant (y compris les contestations et
          questions non contractuelles) sont régis à tous égards par les lois de l'État de New York,
          sans tenir compte des dispositions relatives au conflit de lois, à l'exception de la FAA
          qui prévaut en cas de conflit entre les lois de l'État de New York et elle en ce qui
          concerne l'article 14. S'il s'avère que l'article 14 ne s'applique pas à vous ou à votre
          réclamation, ou si vous renoncez à l'arbitrage dans le cadre de l'article 14.3, Tock et
          vous convenez que toute procédure judiciaire (autre que les poursuites pour de petites
          créances) découlant des présentes modalités (y compris leur existence, leur formation,
          leur fonctionnement et leur résiliation) et (ou) des services (y compris les litiges et
          questions non contractuels) doit être engagée exclusivement devant les tribunaux fédéraux
          ou d'État du comté de New York (État de New York). Tock et vous acceptez la compétence
          personnelle de ces tribunaux et le lieu où ils se trouvent.
        </Typography>

        <Typography variant="body02" paragraph mb={2}>
          <b>16. Modalités supplémentaires</b>
        </Typography>
        <Typography component="ul" paragraph mb={2}>
          <Typography component="li" css={sectionListItem}>
            <b>16.1. Nos applications mobiles.</b> Vous reconnaissez que les présentes modalités
            constituent un contrat juridique contraignant entre Tock et vous uniquement, et non avec
            une société de boutiques d'applications mobiles telle qu'Apple Inc. ou Google LLC, et
            que Tock, et non une telle société de boutiques d'applications, est seule responsable
            des services, de leur contenu et de leur support.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>16.2. Communications électroniques.</b> Tock communiquera avec vous par des moyens
            électroniques, y compris mais sans s'y limiter, par courriel, message texte ou
            notification poussée. Vous acceptez par la présente que tous les avis, divulgations,
            conventions et autres communications que nous vous fournissons par voie électronique
            satisfassent à toute exigence légale selon laquelle ces communications doivent être
            effectuées par écrit.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>16.3. Intégralité de la convention.</b> Les présentes modalités constituent
            l'intégralité de la convention entre Tock et vous concernant l'objet des présentes
            modalités, et elles annulent et remplacent toute autre convention antérieure ou
            actuelle, ou toutes les autres modalités applicables à l'objet des présentes. Vous
            reconnaissez ne vous être appuyé sur aucun terme ni aucune modalité, déclaration,
            garantie ou représentation, à l'exception de ceux qui sont expressément énoncés dans les
            présentes modalités. Vous reconnaissez aussi n'avoir aucun recours concernant tout
            terme, toute modalité, déclaration, garantie ou représentation. Vous pouvez également
            être soumis à des modalités, politiques ou conventions supplémentaires qui peuvent
            s'appliquer lorsque vous utilisez d'autres services, y compris des services de tiers.
            Les présentes modalités ne créent aucun droit de tiers, et aucun tiers n'a le droit ou
            la capacité de revendiquer un droit de propriété ou d'intenter une action pour faire
            appliquer les présentes modalités (sauf si cela est expressément envisagé dans les
            présentes ou s'il en est convenu autrement dans des modalités supplémentaires entre vous
            et une société affiliée de Tock qui énoncent les droits de tiers de cette affiliée de
            Tock pour faire appliquer les présentes modalités).
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>16.4. Renonciation, divisibilité et cession.</b> Le fait que nous n'appliquions pas
            une disposition des présentes modalités ou que nous tardions à le faire ne constitue pas
            une renonciation à notre droit de le faire ultérieurement. Si une disposition des
            présentes modalités est jugée inapplicable, les autres dispositions restent pleinement
            en vigueur et sont remplacées par une disposition applicable reflétant le plus
            fidèlement possible notre intention. Vous ne pouvez pas déléguer, transférer ou céder
            les présentes modalités ou l'un de vos droits ou obligations en vertu des présentes sans
            notre consentement écrit préalable; toute tentative de ce type sera sans effet. Nous
            pouvons déléguer, transférer ou céder les présentes modalités ou l'ensemble ou une
            partie de nos droits et obligations en vertu des présentes, à notre seule discrétion, à
            l'une de nos sociétés affiliées ou filiales ou à tout acquéreur de l'une de nos
            activités ou de l'un de nos actifs associés aux services, moyennant un préavis écrit de
            trente (30) jours.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>16.5. Modifications.</b> Nous pourrons modifier les présentes modalités de temps à
            autre et nous publierons la version la plus récente sur notre site. Si une modification
            réduit sensiblement vos droits, nous vous en informerons (par exemple, en vous envoyant
            un courriel ou en affichant un avis bien visible dans les services). L'avis indiquera
            une période raisonnable après laquelle les nouvelles modalités prendront effet. Les
            modifications ne s'appliqueront pas rétroactivement. En continuant à utiliser les
            services ou à y accéder après l'entrée en vigueur de toute modification, vous acceptez
            d'être lié par la convention modifiée et les changements de prix. Si vous n'êtes pas
            d'accord avec nos modifications, vous devez cesser d'utiliser les services et annuler
            toutes les réservations.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>16.6. Événements indépendants de notre volonté.</b> Nous n'enfreignons pas les
            présentes modalités et ne sommes pas responsables envers vous en cas de défaillance
            totale ou partielle des services résultant d'un acte, d'une circonstance, d'un événement
            ou d'une question échappant à notre contrôle raisonnable. Cela peut comprendre des
            situations faisant suite à un cas de force majeure; un incendie; une mesure
            gouvernementale ou d'un État, ou une réglementation; une guerre; des mouvements
            populaires; des actes de terrorisme; une pandémie; une insurrection; une incapacité à
            communiquer avec des tiers pour quelque raison que ce soit; une défaillance de tout
            système informatique ou nécessaire; une défaillance ou un retard dans la transmission
            des communications; une défaillance de tout fournisseur de services Internet; une grève;
            une action industrielle ou un lock-out; ou toute autre raison échappant à notre contrôle
            raisonnable.
          </Typography>
          <Typography component="li" css={sectionListItem}>
            <b>16.7. Traduction.</b> Les présentes modalités ont été rédigées à l'origine en
            anglais. Nous pourrons les traduire dans d'autres langues. En cas de conflit entre une
            version traduite et la version anglaise, la version anglaise prévaut, sauf si la loi
            applicable l'interdit.
          </Typography>
        </Typography>

        {hasTwilioDirectBrandUpdatesFlag && (
          <>
            <Typography variant="body02" paragraph mb={2}>
              <b>17. Conditions pour messages SMS</b>
            </Typography>
            <Typography component="ul" paragraph mb={2}>
              <Typography component="li" css={sectionListItem}>
                <b>17.1</b> En fournissant votre numéro de téléphone mobile, vous acceptez les{' '}
                {termsLink(`Conditions d’utilisation`)} et la{' '}
                {privacyPolicyLink(`Politique de Confidentialité`)}, et votre numéro de téléphone
                mobile pourrait être utilisé pour vous envoyer des messages texte, vous appeler à
                l’aide d’un système de composition automatique et vous envoyer des messages vocaux
                et des messages texte artificiels ou préenregistrés pour confirmer et gérer des
                réservations.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>17.2</b> Vous pouvez vous désabonner des messages SMS à tout moment. Répondez «
                STOP » à tout moment pour vous désabonner des messages SMS. Une fois que vous aurez
                envoyé le message SMS « STOP », un message SMS vous sera envoyé pour confirmer votre
                désabonnement. Après quoi, vous ne recevrez plus de messages SMS de notre part. Pour
                vous réabonner, répondez « START » et nous recommencerons à vous envoyer des
                messages SMS.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>17.3</b> Si vous éprouvez des problèmes concernant le programme de messages, vous
                pouvez{' '}
                <Anchor href="https://www.surveymonkey.com/r/VQGTSY5">
                  obtenir de l’aide ici.
                </Anchor>
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>17.4</b> Les fournisseurs de services mobiles et Tock ne sont pas responsables
                des messages reçus en retard ou non transmis.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>17.5</b> Comme toujours, des frais pour messages et données peuvent s’appliquer
                pour tout message que nous vous envoyons et que vous nous envoyez. La fréquence
                d’envoi de messages varie. Pour toute question concernant votre forfait de messages
                texte ou de données, communiquez avec votre fournisseur de services mobiles.
              </Typography>
              <Typography component="li" css={sectionListItem}>
                <b>17.6</b> Si vous avez des questions concernant la confidentialité, veuillez lire
                notre {privacyPolicyLink('Politique de Confidentialité')}.
              </Typography>
            </Typography>
          </>
        )}
      </Container>
    </div>
  );
};

export default Terms;
